import { defineStore } from 'pinia';
import Dexie from 'dexie';
import { MerkleJson } from 'merkle-json';
import { toRaw, ref } from 'vue';
import { v4 } from 'uuid';
import * as Yup from 'yup';
import { isFunction } from 'lodash';
import { createTranslator } from 'short-uuid';
import { openobserveLogs } from '@openobserve/browser-logs';
import { openobserveRum } from '@openobserve/browser-rum';
import { UAParser } from 'ua-parser-js';

const ppapiClient = {
    get: request$1('GET'),
    getRaw: request$1('GET',{raw: true}),
    post: request$1('POST'),
    postRaw: request$1('POST',{raw: true}),
    put: request$1('PUT'),
    putRaw: request$1('PUT',{raw: true}),
    delete: request$1('DELETE'),
    deleteRaw: request$1('DELETE',{raw: true}),
    auth: auth()
};

function auth() {
    return async (user, password) => {

        if (typeof(useConfigStore) != "function") {
            logger.warn("configStore not defined");
            return  Promise.reject()
        }

        const configStore = useConfigStore();
        var url = configStore.apiUrl + configStore.authPath;
        const method = 'POST';

        const requestOptions = {
            method,
            headers: authHeader$1(url, configStore.apiAuthHeader)
        };
        if (user && password) {
            const body = { username: user, password: password };
            requestOptions.headers['Content-Type'] = 'application/json';
            requestOptions.credentials = "include";
            requestOptions.body = JSON.stringify(body);
        }

        return fetch(url, requestOptions).then(handleResponse$1);
    }
}

function request$1(method, options) {
    return async (path, body, headers, queries) => {

        if (typeof(useConfigStore) != "function") {
            logger.warn("configStore not defined");
            return  Promise.resolve({error:"configStore not defined"})
        }
        
        const configStore =  useConfigStore();
        const authStore = useAuthStore();

        if (!configStore.apiUrl) {
            logger.warn("no apiUrl defined");
            return  Promise.resolve({error:"no apiUrl defined"})
        }

        // Session- und Standard-dbKey getrennt initialisieren.
        if (!authStore.setup?.sessionDbKey) authStore.setup.sessionDbKey = configStore.defaultDbKey;
        if (!authStore.setup?.dbKey) authStore.setup.dbKey = configStore.defaultDbKey;

        // with global variable window.xdebug set we can enable xdebug session for api requests
        if (!Array.isArray(queries)) queries = [];
        if (window.xdebug) queries.push({XDEBUG_SESSION: window.xdebug});

        let query_string = "";
        let sep = "?";
        if (Array.isArray(queries)) {
            queries.forEach((q) => {
                let par = Object.keys(q)[0];
                if (typeof(par) != "undefined") {
                    query_string += sep + par + "=" + q[par];
                    sep = "&";
                }
            });
        }

        if (path.search("/private/")>-1) {
            let kid = 0;
            if (authStore.user?.kid) {
                kid = authStore.user.kid;
            } 
            path = path.replace("/private/",`/${kid}/`);
        }
        
        let pathComp = path.split("/");
        if (pathComp[0] == "") {
            var basePath = pathComp[1];
            var secondaryPath = pathComp[2];
        } else {
            basePath = pathComp[0];
            secondaryPath = pathComp[1];
        }

        const isLoggedIn = !!authStore.user?.token;
        const isSessionEndpoint = typeof path == "string"
            && (path.indexOf("/session/") > -1 || path.indexOf("session/") == 0);

        let apiPrefix = "";

        // Session-dbKey nutzen, wenn User ausgeloggt ist oder expliziter Session-Endpoint aufgerufen wird.
        if (!isLoggedIn || isSessionEndpoint) {
            if (authStore.setup?.sessionDbKey) {
                apiPrefix = "/" + authStore.setup.sessionDbKey;
            } else if (authStore.setup?.dbKey) {
                apiPrefix = "/" + authStore.setup.dbKey;
            }
        } else {
            if (authStore.user?.dbKey) {
                apiPrefix = "/" + authStore.user.dbKey;
            } else if (authStore.setup?.dbKey) {
                apiPrefix = "/" + authStore.setup.dbKey;
            }
        }

        if (basePath == "storage") {
            console.log("storage url",path,apiPrefix,configStore.apiUrl);
            var url = configStore.apiUrl + apiPrefix + '/' + path + query_string;
        } else if (secondaryPath == "storage") {
            console.log("storage url with dbkey",path,apiPrefix,configStore.apiUrl);
            url = configStore.apiUrl + '/' + path + query_string;
        } else {
            console.log("request url",path,apiPrefix,configStore.apiUrl);
            // double slash means that path is the full path after api url, so we can use it directly (for example for media contentUrl)
            if (path.substr(0,2) == "//") url = configStore.apiUrl + path.substr(1) + query_string;
            else // otherwise we add api prefix and path to api url       
            url = configStore.apiUrl + apiPrefix + configStore.apiPath + '/' + path + query_string;
        }
        

        const requestOptions = {
            method,
            headers: authHeader$1(url, configStore.apiAuthHeader),
            credentials: 'include'
        };

        if (headers) {
            for (var i in headers) {
                requestOptions.headers[i] = headers[i];
            }
        }
        if (body) {
            requestOptions.headers['Content-Type'] = 'application/json';
            requestOptions.body = JSON.stringify(body);
        }

        if (options?.raw) return fetch(url, requestOptions)
        else return fetch(url, requestOptions).then(handleResponse$1);

    }
}

// Wrapper Tasks
/*
* example :
* ppapiClient.saveMedia({module:'kalender',previews:[{dataUrl:'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAFCAYAAACNbyblAAAAHElEQVQI12P4//8/w38GIAXDIBKE0DHxgljNBAAO9TXL0Y4OHwAAAABJRU5ErkJggg=='}]},{type:'skript',role:'user_design'},{'type':'vic'})
*/
function saveMedia(media,component,componentData) {

    if (typeof componentData == "object") componentData = JSON.stringify(componentData);
    let mediaResource = {...media};
    mediaResource.components = [{...component,scriptData: componentData}];

    if (mediaResource.uid) {
        return ppapiClient.put(`media/${mediaResource.uid}`,mediaResource)
    } else {
        return ppapiClient.post("media",mediaResource)
    }
    
}

ppapiClient.saveMedia = saveMedia;

// helper functions

function authHeader$1(url, withAuth) {
    // return auth header with jwt if user is logged in and request is to the api url
    if (typeof(useAuthStore) != "function") return  {}

    const { user } = useAuthStore();
    const configStore = useConfigStore();
    const isLoggedIn = !!user?.token;
    const headers = {};

    if (configStore.xApiToken) {
        headers['X-API-TOKEN'] = configStore.xApiToken;
    }

    const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL);
    if ((isLoggedIn && isApiUrl) || withAuth) {
        headers['Authorization'] = `Bearer ${user.token}`;
    }
    return headers;
}

async function handleResponse$1(response) {
    const isJson = response.headers?.get('content-type')?.includes('application/json');
    const isJsonLd = response.headers?.get('content-type')?.includes('application/ld+json');
    const data = (isJson || isJsonLd) ? await response.json() : null;

    // check for error response
    if (!response.ok) {
        const { user, logout } = useAuthStore();
        if ([401, 403].includes(response.status) && user) {
            // auto logout if 401 Unauthorized or 403 Forbidden response returned from api
            logout(true);
        }

        // Preserve the API error code for client-side localization.
        const message = (data && (data.message || data['hydra:description'])) || response.status;
        const error = new Error(String(message));
        error.status = response.status;
        if (data?.errorCode) error.errorCode = data.errorCode;
        error.response = data;
        return Promise.reject(error);
    }
    if (isJsonLd) {
        const response = new apiResponse(data);
        response.loaded = true;
        return response
    }
    return data;
}

function apiResponse(data) {

    if (!data) data = {};

    this.rawData = data;
    this.loaded = false;

    this.data = function () {
        switch (this.rawData["@type"]) {
            case "hydra:Collection":
                return this.rawData["hydra:member"]
            default:
                return this.rawData

        }

    };


}

async function getMediaContent(id,jid,role) {

    let response = false;
  
    if (!role) role = "user_design";
  
    if (!jid) {
      if (id[0] == "/") {
          response = await request$1("GET")(id);
      } else {
          response = await request$1("GET")("media/" + id);
      }
    } else {
      // id ist KID
      response = await request$1("GET")("users/" + id + "/media/" + jid);
    }
  
    const data = response.data();
    const components = data.components;
    let userDesign = components.find((el) => el.role == role);
  
    if (userDesign.contentUrl) {
        console.log("GET content url","/" +userDesign.contentUrl);
      return request$1("GET",{raw: true})("/" +userDesign.contentUrl).then((res) => {
        console.log("GET content url response",res);
        res.meta = {
            '@id' : data["@id"],
            '@type' : data["@type"],
            id: data.id,
            jid: data.jid,
            kid: data.kid,
            date: data.date,
            userDesign: userDesign
        };
        return res
      })   
    }
    
  }

const fetchWrapper = {
    get: request('GET'),
    post: request('POST'),
    put: request('PUT'),
    delete: request('DELETE')
};

function request(method) {
    return (url, body) => {
        const requestOptions = {
            method,
            headers: authHeader(url)
        };
        if (body) {
            requestOptions.headers['Content-Type'] = 'application/json';
            requestOptions.body = JSON.stringify(body);
        }
        return fetch(url, requestOptions).then(handleResponse);
    }
}

// helper functions

function authHeader(url) {
    // return auth header with jwt if user is logged in and request is to the api url
    const { user } = useAuthStore();
    const isLoggedIn = !!user?.token;
    const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL);
    if (isLoggedIn && isApiUrl) {
        return { Authorization: `Bearer ${user.token}` };
    } else {
        return {};
    }
}

async function handleResponse(response) {
    const contentType = response.headers?.get('content-type') || '';
    const isJson = contentType.includes('application/json') || contentType.includes('application/ld+json');
    const data = isJson ? await response.json() : null;

    // check for error response
    if (!response.ok) {
        const { user, logout } = useAuthStore();
        if ([401, 403].includes(response.status) && user) {
            // auto logout if 401 Unauthorized or 403 Forbidden response returned from api
            logout();
        }

        // Preserve the API error code for client-side localization.
        const message = (data && (data.message || data['hydra:description'])) || response.status;
        const error = new Error(String(message));
        if (data?.errorCode) error.errorCode = data.errorCode;
        error.response = data;
        return Promise.reject(error);
    }

    return data;
}

//import { useConfigStore } from '@/stores';
//const configStore = useConfigStore();

const db = new Dexie('ppDb');

db.version(2).stores({
    products: "pid,  state, pgrId, mediaType", // Primary key and indexed props
    prices: "id, pid, priceGroup",
    productSetup: "name",
    productOptions: "po,pid,name"
});

db.version(3).stores({
    products: "pid,  state, pgrId, mediaType", // Primary key and indexed props
    prices: "id, pid, priceGroup",
    productSetup: "name",
    productOptions: "po,pid,name",
    sync_meta: 'id'
});

const dbPopulate = async function () {

    const resources = [
        { path: 'productpricegroups', table: db.products, tableLabel: 'products', metaId: 'productpricegroups_meta' },
        { path: 'prices', table: db.prices, tableLabel: 'prices', metaId: 'prices_meta' },
        { path: 'productSetup', table: db.productSetup, tableLabel: 'productSetup', metaId: 'productSetup_meta' },
        { path: 'productOptions', table: db.productOptions, tableLabel: 'productOptions', metaId: 'productOptions_meta' }
    ];

    logger.info("Populating local database from API...");

    await Promise.all(resources.map(async function (resource) {
        const meta = await db.sync_meta.get(resource.metaId);
        const headers = { accept: 'application/json' };

        if (meta?.timestamp) {
            headers['If-Modified-Since'] = meta.timestamp;
        }

        const response = await ppapiClient.getRaw(resource.path, false, headers);

        if (response.status === 304) {
            logger.info(`No changes for ${resource.tableLabel} since last sync.`);
            return;
        }

        if (!response.headers?.get('content-type')?.includes('application/json')) {
            logger.warn(`Skipping ${resource.tableLabel}: response is not JSON.`);
            return;
        }

        const data = await response.json();
        logger.debug(`Got API response for ${resource.tableLabel}. Updating local table...`);

        resource.table.clear().then(() => {   

            logger.debug("Calling bulkAdd() to insert productOptions objects...");
        
            if (Array.isArray(data) && data.length > 0) {
                resource.table.bulkAdd(data);
            }
                    
            const lastModified = response.headers?.get('last-modified');
            if (lastModified) {
                db.sync_meta.put({ id: resource.metaId, timestamp: lastModified });
            }

            logger.info(`Done populating ${resource.tableLabel}.`); 
        });

        
    }));
};

db.open();

const joinProducts = function (priceCollection) {

    var all = Dexie.Promise.all;

    return priceCollection.toArray(function (prc) {

        var productPromises = prc.map(function (price) {
            return db.products.get(price.pid || 0)
        });
        //window.productPromises = productPromises // DEBUG

        return all([
            all(productPromises)
        ]).then(function (productPrices) {
            prc.forEach(function (price, i) {
                price.product = productPrices[0][i];
            });
            return prc
        })
    })
};

const mj = new MerkleJson();

function fkEl(type, id, name, label, options, t = (key) => key, settings = {}) {

    let classes = {};
    if (!options) options = {};
    
    
    if (type == "radio-float") {
        classes["outer"] = ["radio-float", settings.class].filter(Boolean).join(" ");
        type = "radio";
    } else {
        classes["outer"] = settings.class || [];
    }

    classes["outer"] += " ps-sel-" + name;

    let child = {
        $formkit: type,
        name: name,
        key: id,
        options: options,
        placeholder: t('formkitElement.choose option', {option: label}),
        classes: classes,
        help: "",
    };

    if (settings.title) child.label = settings.title;

    let schemaEl =
    {
       
        $el: "div",
        children: [
            child
        ],

        getOptions: function () {

            return this.children.find(el => el.$formkit).options
        },

        setOptions: function (opt) {

            let child = this.children.find(el => el.$formkit);

            child.options = toRaw(opt);
            child.key = mj.hash(opt);
        },

        setHelp: function (help) {

            this.children.find(el => el.$formkit).help = help;
        },

        addConfirm: function () {

            this.children.push({
                $formkit: "confirm",
                name: "confirm_" + name,
                key: id,
                options: [{confirmed: t('formkitElement.confirm')}],
                label: t('formkitElement.confirm'),
                
            });
        }
    };

    return schemaEl
}

const formkitExtension = () => { };
// But we do want to attach a “library”

formkitExtension.library = (node) => {

    // input ist ein unsichtbares textfeld, welches den wert das 
    // ausgewählten tabs erhält (a.data-value)
    // const text = {
    // $el: 'input',
    // attrs: {
    //   onInput: (data) => {
    //     console.log(data.target.id + ' ' + data.target.value)
    // },
    //  // value: '$_value',
    //  // style:  { display: 'none' },
    // },
    //}

    let children = [];

    let activeEl = [];        
    let listElement = [];
    const selectorClass = "ps-sel-" + node.name;

    switch (node.props.type) {
        
        case 'tab':

            if (node.props.attrs && node.props.attrs.options) {
                for (let i in node.props.attrs.options) {
                    let el = {
                        $el: 'li',
                        attrs: {class: (i==node.props.attrs.selected)?"uk-active":""}, // set initial selection
                        key: v4(), // Wichtig, sonst funktioniert bei gleichem "name" 2-way-bind nicht mehr 
                        children: [{
                            $el: 'a',
                            children: [node.props.attrs.options[i]],
                            attrs: {
                                "data-value": i,
                                onclick: (a) => {
                                    //myInput.value.node.value[node.name] = a.target.dataset.value
                                    // parentNode.find('ratios').input(a.target.dataset.value)          
                                    node.input(a.target.dataset.value);
                                }
                            }
                        }]
                    };
                    children.push(el);
                }
            }
            return node.define({
                type: 'input',
                schema: [{
                    $el: "ul",
                    attrs: {
                        class: "uk-tab",
                        "data-uk-tab": ""
                    },
                    children: children
                }],
            })

        case 'dropgrid2':

            
            if (node.props.attrs && node.props.attrs.options) {
                for (let i in node.props.attrs.options) {
                    let title = {
                        $el: "span",
                        if: '$value == ' + i,
                        children: [node.props.attrs.options[i]]
                    };
                    let element = {
                        $el: 'div',
                        key: v4(), // Wichtig, sonst funktioniert bei gleichem "name" 2-way-bind nicht mehr 
                        children: [{
                            $el: 'a',
                            children: [node.props.attrs.options[i]],
                            attrs: {
                                "data-value": i,
                                class: "",
                                onclick: (a) => {
                                    //myInput.value.node.value[node.name] = a.target.dataset.value
                                    // parentNode.find('ratios').input(a.target.dataset.value)          
                                    node.input(a.target.dataset.value);
                                }
                            }
                        }]
                    };
                    activeEl.push(title);
                    listElement.push(element);
                }
            }

            return node.define({
                type: 'input',
                schema: [
                    {
                        $el: "nav",
                        attrs: {
                            "uk-dropnav": "",
                            class: selectorClass
                        },
                        children: [{
                            $el: 'div',
                            attrs: {
                                class: "uk-subnav"
                            },
                            children: [{
                                $el: 'span',
                                attrs: {
                                    class: "uk-active"
                                },
                                children: [{
                                    $el: 'a',
                                    children: activeEl
                                }]
                            },
                            {
                                $el: "span",
                                attrs: {},
                                children: [{
                                    $el: 'a',
                                    children: [{
                                        $el: 'span',
                                        attrs: { 'uk-drop-parent-icon': '' }
                                    }]
                                },
                                {
                                    $el: 'div',
                                    attrs: { class: "uk-width-large", 'uk-dropdown': '' },
                                    children: [{
                                        $el: 'div',
                                        attrs: { class: "uk-grid uk-child-width-1-2@m" },
                                        children: listElement
                                    }]
                                }
                                ]
                            }]
                        }]

                    }]
            })

            case 'icon':


            if (node.props.attrs && node.props.attrs.options) {
                for (let i in node.props.attrs.options) {
                    let label = node.props.attrs.options[i];

                    let element = {
                        $el: 'div',
                        attrs: { 
                            class: "fk-select-image " + selectorClass, 
                            "data-value": i,
                            onclick: (a) => {
                                //myInput.value.node.value[node.name] = a.target.dataset.value
                                // parentNode.find('ratios').input(a.target.dataset.value)          
                                node.input(a.target.dataset.value);
                            }
                        },
                        children: [
                            {
                                $el: "div",
                                if: '$value == ' + i,
                                attrs: {
                                    class: "img-checked"
                                },
                                children: [{$el: 'i', attrs: {'uk-icon': "icon: check"}}]
                            },
                            {
                            $el: 'div',
                            attrs: {
                                class: "fk-image-label"
                            },
                            children: label
                        }], 
                        key: v4(), // Wichtig, sonst funktioniert bei gleichem "name" 2-way-bind nicht mehr 
                        
                    };

                    listElement.push(element);
                }
            }

            return node.define({
                type: 'input',
                schema: [
                    {
                        $el: 'div',
                        children: [{
                            $el: 'div',
                            attrs: { class: "fk-icon-select" },
                            children: listElement
                        }]
                    }
                ]
            })

            case 'confirm':


            if (node.props.attrs && node.props.attrs.options) {
                for (let i in node.props.attrs.options) {

                    let element = {
                        $el: 'button',
                        attrs: { 
                            class: "uk-button", 
                            "data-value": node.name,
                            href: "#",
                            onclick: (a) => {
                                //myInput.value.node.value[node.name] = a.target.dataset.value
                                // parentNode.find('ratios').input(a.target.dataset.value)
                                a.preventDefault();          
                                node.input(true);
                            }
                        },
                        children: [{
                            if: '$value == false',
                            then: {
                                $el: 'span',
                                attrs: {
                                 'uk-icon': "icon: play" 
                                },
                            },
                            else: {
                                $el: 'span',
                                attrs: {
                                 'uk-icon': "icon: check" 
                                },        
                            }                        
                        },
                        {
                            $el: 'span',
                            children: node.props.label,
                            attrs: {
                                class: "label" 
                               }
                        }
                      ],

                        key: v4(), // Wichtig, sonst funktioniert bei gleichem "name" 2-way-bind nicht mehr 
                        
                    };

                    listElement.push(element);
                }
            }

            return node.define({
                type: 'input',
                schema: [
                    {
                        $el: 'div',
                        children: [{
                            $el: 'div',
                            attrs: { class: "fk-confirmc " + selectorClass },
                            children: listElement
                        }]
                    }
                ]
            })

    }
};

const addressSchema = Yup.object().shape({
    firstname: Yup.string()
        .required(' erforderlich'),
    lastname: Yup.string()
        .required('erforderlich'),
    street: Yup.string()
        .required('erforderlich'),
    zip: Yup.string()
        .required('erforderlich'),
    city: Yup.string()
        .required('erforderlich'),
    countryISO: Yup.string()
        .required('erforderlich')
        .length(2, 'muss 2 Zeichen lang sein'),
});

const bankaccountSchema = Yup.object().shape({
    owner: Yup.string()
        .trim()
        .required('erforderlich'),
    iban: Yup.string()
        .transform((value) => String(value || '').replace(/\s+/g, '').toUpperCase())
        .required('erforderlich')
        .matches(/^[A-Z]{2}[0-9A-Z]{13,32}$/, 'ungültige IBAN')
});

const emailSchema = Yup.string()
    .trim()
    .required('erforderlich')
    .email('ungueltige Emailadresse');

function isValidEmail(email) {
    return emailSchema.isValidSync(String(email || '').trim());
}

const LOCAL_UPLOAD_EVENT = 'pp-shop:local-upload-success';
const DROPZONE_UPLOAD_EVENT = 'pp-shop:dropzone-upload';

function getPpShopEventBus() {
    if (typeof window === 'undefined') return null
    if (!window.ppShop) window.ppShop = {};
    if (!window.ppShop.events) window.ppShop.events = new EventTarget();
    return window.ppShop.events
}

function dispatchPpShopEvent(eventName, detail) {
    const eventBus = getPpShopEventBus();
    if (!eventBus) return false
    return eventBus.dispatchEvent(new CustomEvent(eventName, { detail }))
}

function registerPpShopEvent(eventName, callback) {
    const eventBus = getPpShopEventBus();
    if (!eventBus) return () => {}

    eventBus.addEventListener(eventName, callback);
    return () => eventBus.removeEventListener(eventName, callback)
}

const shortUuidTranslator = createTranslator({
    uuid: () => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID()
        }
        return v4()
    }
});

class UploadModal {

    constructor() {
        this.show = ref(false);
        this.variant = ref('dashboard');
        this.closeMode = ref('manual');
        this.title = ref({ text: '', display: '' });
        this.closeButton = ref({ text: '', display: '' });
    }

    open(setup) {
        this.setParameter(setup);
        this.show.value = true;
        return false
    }
  
    close() {
        this.show.value = false;
        return false
    }

    setParameter(setup) {
        this.setup = setup;
        this.variant.value = setup?.variant || 'dashboard';
        this.closeMode.value = setup?.close == 'auto' ? 'auto' : 'manual';
        this.title.value = {
            text: typeof setup?.title?.text === 'string' ? setup.title.text : '',
            display: setup?.title?.display == 'none' ? 'none' : '',
        };
        this.closeButton.value = {
            text: typeof setup?.closeButton?.text === 'string' ? setup.closeButton.text : '',
            display: setup?.closeButton?.display == 'none' ? 'none' : '',
        };
    }

    onUpload(payload) {
        dispatchPpShopEvent(LOCAL_UPLOAD_EVENT, payload);

        // callback if defined on setup
        if (this.setup && typeof this.setup.onUpload == "function") {
            this.setup.onUpload(payload);
        }
    }
}

class MediaModal {

    constructor() {
        this.show = ref(false);
        this.selectedMedia = [];
        this.setup = {
            MediaManager: {}
        };
    }

    open(setup) {
        this.setParameter(setup);
        this.show.value = true;
        return false
    }
  
    close() {
        this.show.value = false;
        return false
    }

    setParameter(setup) {
        this.setup = { ...this.setup, ...setup };
    }

    onMediaOpen() {
        // callback if defined on setup
        if (this.setup && typeof this.setup.onMediaOpen== "function") {
            this.setup.onMediaOpen(this.selectedMedia);
        }
        this.close();
    }

    onSelect(data) {
        // callback if defined on setup
        if (this.setup && typeof this.setup.onSelect== "function") {
            this.setup.onSelect(data);
        }
    }
}

function generateUUID(prefix) { // Public Domain/MIT
    var d = new Date().getTime();
    if (!prefix) prefix = "";
    if (typeof performance !== 'undefined' && typeof performance.now === 'function'){
        d += performance.now(); //use high-precision timer if available
    }
    var newGuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
    
    return prefix + newGuid;
}

function generateShortUUID(prefix = "") {
    try {
        return `${prefix}${shortUuidTranslator.generate()}`
    } catch {
        return `${prefix}${generateUUID()}`
    }
}

// Erstellt eine neue Globale Variable
// oder liefert dessen referenz zurück
function glob(name, value, keep) {

    if (!window.ppShop) window.ppShop = {};
    if (typeof value != "undefined" && (!window.ppShop[name] || keep===false )) {       
        if (isFunction(value))  {
            if (isClass(value)) {
                window.ppShop[name] = new value();
            } else {
                window.ppShop[name] = value();
            }
        } else {
            window.ppShop[name] = value;
        }

        if (name === 'mercureEvents') {
            dispatchPpShopEvent('pp-shop:mercure-events-ready');
        }
    }
    return window.ppShop[name]
    
}

function globalize(name,obj) {

    if (!window.ppShop) window.ppShop = {};
    window.ppShop[name] = obj;

    return window.ppShop[name]
}

function createMercureAutoBinder(registerMethodName, unregisterMethodName, bindingName, callback, options) {

    let mercureEvents = null;
    let listenerId = null;

    function unbind() {
        if (mercureEvents && listenerId) {
            const unregisterArgument = unregisterMethodName == 'removeHook'
                ? bindingName
                : listenerId;
            mercureEvents[unregisterMethodName](unregisterArgument);
        }
        listenerId = null;
    }

    function bind() {
        const currentMercureEvents = glob("mercureEvents");
        if (!currentMercureEvents) return

        if (mercureEvents === currentMercureEvents && listenerId) {
            return
        }

        unbind();
        mercureEvents = currentMercureEvents;
        listenerId = mercureEvents[registerMethodName](bindingName, callback, options);
    }

    const onMercureEventsReady = () => {
        bind();
    };

    const onMercureEventsRecreated = () => {
        bind();
    };

    bind();
    const releaseMercureEventsReady = registerPpShopEvent('pp-shop:mercure-events-ready', onMercureEventsReady);
    const releaseMercureEventsRecreated = registerPpShopEvent('pp-shop:mercure-events-recreated', onMercureEventsRecreated);

    return () => {
        releaseMercureEventsReady();
        releaseMercureEventsRecreated();
        unbind();
        mercureEvents = null;
    }
}

// Registriert ein Mercure-Event robust gegen Reconnects mit neuer Instanz.
// Rückgabewert ist eine Cleanup-Funktion für onBeforeUnmount/close().
function registerMercureEvent(eventName, callback, options) {
    return createMercureAutoBinder('register', 'unregister', eventName, callback, options)
}

// Registriert einen Mercure-Hook robust gegen Reconnects mit neuer Instanz.
// Rückgabewert ist eine Cleanup-Funktion für onBeforeUnmount/close().
function registerMercureHook(hookName, callback, options) {
    return createMercureAutoBinder('addHook', 'removeHook', hookName, callback, options)
}

function registerLocalUploadEvent(callback) {
    const listener = (event) => callback(event.detail);
    return registerPpShopEvent(LOCAL_UPLOAD_EVENT, listener)
}

function isClass(v) {
    return typeof v === 'function' && /^\s*class\s+/.test(v.toString());
}

const uploadModal = new UploadModal();
const mediaModal = new MediaModal();

globalize("mediaModal",mediaModal);

/** Internal global with common properties and Sentry extensions  */

/** Get's the global object for the current JavaScript runtime */
const GLOBAL_OBJ = globalThis ;

/**
 * This serves as a build time flag that will be true by default, but false in non-debug builds or if users replace `__SENTRY_DEBUG__` in their generated code.
 *
 * ATTENTION: This constant must never cross package boundaries (i.e. be exported) to guarantee that it can be used for tree shaking.
 */
const DEBUG_BUILD = (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__);

// This is a magic string replaced by rollup

const SDK_VERSION = "9.14.0" ;

/**
 * An object that contains globally accessible properties and maintains a scope stack.
 * @hidden
 */

/**
 * Returns the global shim registry.
 *
 * FIXME: This function is problematic, because despite always returning a valid Carrier,
 * it has an optional `__SENTRY__` property, which then in turn requires us to always perform an unnecessary check
 * at the call-site. We always access the carrier through this function, so we can guarantee that `__SENTRY__` is there.
 **/
function getMainCarrier() {
  // This ensures a Sentry carrier exists
  getSentryCarrier(GLOBAL_OBJ);
  return GLOBAL_OBJ;
}

/** Will either get the existing sentry carrier, or create a new one. */
function getSentryCarrier(carrier) {
  const __SENTRY__ = (carrier.__SENTRY__ = carrier.__SENTRY__ || {});

  // For now: First SDK that sets the .version property wins
  __SENTRY__.version = __SENTRY__.version || SDK_VERSION;

  // Intentionally populating and returning the version of "this" SDK instance
  // rather than what's set in .version so that "this" SDK always gets its carrier
  return (__SENTRY__[SDK_VERSION] = __SENTRY__[SDK_VERSION] || {});
}

/**
 * Returns a global singleton contained in the global `__SENTRY__[]` object.
 *
 * If the singleton doesn't already exist in `__SENTRY__`, it will be created using the given factory
 * function and added to the `__SENTRY__` object.
 *
 * @param name name of the global singleton on __SENTRY__
 * @param creator creator Factory function to create the singleton if it doesn't already exist on `__SENTRY__`
 * @param obj (Optional) The global object on which to look for `__SENTRY__`, if not `GLOBAL_OBJ`'s return value
 * @returns the singleton
 */
function getGlobalSingleton(
  name,
  creator,
  obj = GLOBAL_OBJ,
) {
  const __SENTRY__ = (obj.__SENTRY__ = obj.__SENTRY__ || {});
  const carrier = (__SENTRY__[SDK_VERSION] = __SENTRY__[SDK_VERSION] || {});
  // Note: We do not want to set `carrier.version` here, as this may be called before any `init` is called, e.g. for the default scopes
  return carrier[name] || (carrier[name] = creator());
}

/** Prefix for logging strings */
const PREFIX = 'Sentry Logger ';

const CONSOLE_LEVELS = [
  'debug',
  'info',
  'warn',
  'error',
  'log',
  'assert',
  'trace',
] ;

/** This may be mutated by the console instrumentation. */
const originalConsoleMethods

 = {};

/** A Sentry Logger instance. */

/**
 * Temporarily disable sentry console instrumentations.
 *
 * @param callback The function to run against the original `console` messages
 * @returns The results of the callback
 */
function consoleSandbox(callback) {
  if (!('console' in GLOBAL_OBJ)) {
    return callback();
  }

  const console = GLOBAL_OBJ.console ;
  const wrappedFuncs = {};

  const wrappedLevels = Object.keys(originalConsoleMethods) ;

  // Restore all wrapped console methods
  wrappedLevels.forEach(level => {
    const originalConsoleMethod = originalConsoleMethods[level] ;
    wrappedFuncs[level] = console[level] ;
    console[level] = originalConsoleMethod;
  });

  try {
    return callback();
  } finally {
    // Revert restoration to wrapped state
    wrappedLevels.forEach(level => {
      console[level] = wrappedFuncs[level] ;
    });
  }
}

function makeLogger() {
  let enabled = false;
  const logger = {
    enable: () => {
      enabled = true;
    },
    disable: () => {
      enabled = false;
    },
    isEnabled: () => enabled,
  };

  if (DEBUG_BUILD) {
    CONSOLE_LEVELS.forEach(name => {
      logger[name] = (...args) => {
        if (enabled) {
          consoleSandbox(() => {
            GLOBAL_OBJ.console[name](`${PREFIX}[${name}]:`, ...args);
          });
        }
      };
    });
  } else {
    CONSOLE_LEVELS.forEach(name => {
      logger[name] = () => undefined;
    });
  }

  return logger ;
}

/**
 * This is a logger singleton which either logs things or no-ops if logging is not enabled.
 * The logger is a singleton on the carrier, to ensure that a consistent logger is used throughout the SDK.
 */
const logger$1 = getGlobalSingleton('logger', makeLogger);

// eslint-disable-next-line @typescript-eslint/unbound-method
const objectToString = Object.prototype.toString;
/**
 * Checks whether given value is an instance of the given built-in class.
 *
 * @param wat The value to be checked
 * @param className
 * @returns A boolean representing the result.
 */
function isBuiltin(wat, className) {
  return objectToString.call(wat) === `[object ${className}]`;
}

/**
 * Checks whether given value's type is an object literal, or a class instance.
 * {@link isPlainObject}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
function isPlainObject(wat) {
  return isBuiltin(wat, 'Object');
}

/**
 * Checks whether given value has a then function.
 * @param wat A value to be checked.
 */
function isThenable(wat) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  return Boolean(wat?.then && typeof wat.then === 'function');
}

/**
 * Truncates given string to the maximum characters count
 *
 * @param str An object that contains serializable values
 * @param max Maximum number of characters in truncated string (0 = unlimited)
 * @returns string Encoded
 */
function truncate(str, max = 0) {
  if (typeof str !== 'string' || max === 0) {
    return str;
  }
  return str.length <= max ? str : `${str.slice(0, max)}...`;
}

/**
 * Defines a non-enumerable property on the given object.
 *
 * @param obj The object on which to set the property
 * @param name The name of the property to be set
 * @param value The value to which to set the property
 */
function addNonEnumerableProperty(obj, name, value) {
  try {
    Object.defineProperty(obj, name, {
      // enumerable: false, // the default, so we can save on bundle size by not explicitly setting it
      value: value,
      writable: true,
      configurable: true,
    });
  } catch (o_O) {
    DEBUG_BUILD && logger$1.log(`Failed to add non-enumerable property "${name}" to object`, obj);
  }
}

function getCrypto() {
  const gbl = GLOBAL_OBJ ;
  return gbl.crypto || gbl.msCrypto;
}

/**
 * UUID4 generator
 * @param crypto Object that provides the crypto API.
 * @returns string Generated UUID4.
 */
function uuid4(crypto = getCrypto()) {
  let getRandomByte = () => Math.random() * 16;
  try {
    if (crypto?.randomUUID) {
      return crypto.randomUUID().replace(/-/g, '');
    }
    if (crypto?.getRandomValues) {
      getRandomByte = () => {
        // crypto.getRandomValues might return undefined instead of the typed array
        // in old Chromium versions (e.g. 23.0.1235.0 (151422))
        // However, `typedArray` is still filled in-place.
        // @see https://developer.mozilla.org/en-US/docs/Web/API/Crypto/getRandomValues#typedarray
        const typedArray = new Uint8Array(1);
        crypto.getRandomValues(typedArray);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return typedArray[0];
      };
    }
  } catch (_) {
    // some runtimes can crash invoking crypto
    // https://github.com/getsentry/sentry-javascript/issues/8935
  }

  // http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/2117523#2117523
  // Concatenating the following numbers as strings results in '10000000100040008000100000000000'
  return (([1e7] ) + 1e3 + 4e3 + 8e3 + 1e11).replace(/[018]/g, c =>
    // eslint-disable-next-line no-bitwise
    ((c ) ^ ((getRandomByte() & 15) >> ((c ) / 4))).toString(16),
  );
}

const ONE_SECOND_IN_MS = 1000;

/**
 * A partial definition of the [Performance Web API]{@link https://developer.mozilla.org/en-US/docs/Web/API/Performance}
 * for accessing a high-resolution monotonic clock.
 */

/**
 * Returns a timestamp in seconds since the UNIX epoch using the Date API.
 */
function dateTimestampInSeconds() {
  return Date.now() / ONE_SECOND_IN_MS;
}

/**
 * Returns a wrapper around the native Performance API browser implementation, or undefined for browsers that do not
 * support the API.
 *
 * Wrapping the native API works around differences in behavior from different browsers.
 */
function createUnixTimestampInSecondsFunc() {
  const { performance } = GLOBAL_OBJ ;
  if (!performance?.now) {
    return dateTimestampInSeconds;
  }

  // Some browser and environments don't have a timeOrigin, so we fallback to
  // using Date.now() to compute the starting time.
  const approxStartingTimeOrigin = Date.now() - performance.now();
  const timeOrigin = performance.timeOrigin == undefined ? approxStartingTimeOrigin : performance.timeOrigin;

  // performance.now() is a monotonic clock, which means it starts at 0 when the process begins. To get the current
  // wall clock time (actual UNIX timestamp), we need to add the starting time origin and the current time elapsed.
  //
  // TODO: This does not account for the case where the monotonic clock that powers performance.now() drifts from the
  // wall clock time, which causes the returned timestamp to be inaccurate. We should investigate how to detect and
  // correct for this.
  // See: https://github.com/getsentry/sentry-javascript/issues/2590
  // See: https://github.com/mdn/content/issues/4713
  // See: https://dev.to/noamr/when-a-millisecond-is-not-a-millisecond-3h6
  return () => {
    return (timeOrigin + performance.now()) / ONE_SECOND_IN_MS;
  };
}

/**
 * Returns a timestamp in seconds since the UNIX epoch using either the Performance or Date APIs, depending on the
 * availability of the Performance API.
 *
 * BUG: Note that because of how browsers implement the Performance API, the clock might stop when the computer is
 * asleep. This creates a skew between `dateTimestampInSeconds` and `timestampInSeconds`. The
 * skew can grow to arbitrary amounts like days, weeks or months.
 * See https://github.com/getsentry/sentry-javascript/issues/2590.
 */
const timestampInSeconds = createUnixTimestampInSecondsFunc();

/**
 * Updates a session object with the properties passed in the context.
 *
 * Note that this function mutates the passed object and returns void.
 * (Had to do this instead of returning a new and updated session because closing and sending a session
 * makes an update to the session after it was passed to the sending logic.
 * @see Client.captureSession )
 *
 * @param session the `Session` to update
 * @param context the `SessionContext` holding the properties that should be updated in @param session
 */
// eslint-disable-next-line complexity
function updateSession(session, context = {}) {
  if (context.user) {
    if (!session.ipAddress && context.user.ip_address) {
      session.ipAddress = context.user.ip_address;
    }

    if (!session.did && !context.did) {
      session.did = context.user.id || context.user.email || context.user.username;
    }
  }

  session.timestamp = context.timestamp || timestampInSeconds();

  if (context.abnormal_mechanism) {
    session.abnormal_mechanism = context.abnormal_mechanism;
  }

  if (context.ignoreDuration) {
    session.ignoreDuration = context.ignoreDuration;
  }
  if (context.sid) {
    // Good enough uuid validation. — Kamil
    session.sid = context.sid.length === 32 ? context.sid : uuid4();
  }
  if (context.init !== undefined) {
    session.init = context.init;
  }
  if (!session.did && context.did) {
    session.did = `${context.did}`;
  }
  if (typeof context.started === 'number') {
    session.started = context.started;
  }
  if (session.ignoreDuration) {
    session.duration = undefined;
  } else if (typeof context.duration === 'number') {
    session.duration = context.duration;
  } else {
    const duration = session.timestamp - session.started;
    session.duration = duration >= 0 ? duration : 0;
  }
  if (context.release) {
    session.release = context.release;
  }
  if (context.environment) {
    session.environment = context.environment;
  }
  if (!session.ipAddress && context.ipAddress) {
    session.ipAddress = context.ipAddress;
  }
  if (!session.userAgent && context.userAgent) {
    session.userAgent = context.userAgent;
  }
  if (typeof context.errors === 'number') {
    session.errors = context.errors;
  }
  if (context.status) {
    session.status = context.status;
  }
}

/**
 * Generate a random, valid trace ID.
 */
function generateTraceId() {
  return uuid4();
}

/**
 * Shallow merge two objects.
 * Does not mutate the passed in objects.
 * Undefined/empty values in the merge object will overwrite existing values.
 *
 * By default, this merges 2 levels deep.
 */
function merge(initialObj, mergeObj, levels = 2) {
  // If the merge value is not an object, or we have no merge levels left,
  // we just set the value to the merge value
  if (!mergeObj || typeof mergeObj !== 'object' || levels <= 0) {
    return mergeObj;
  }

  // If the merge object is an empty object, and the initial object is not undefined, we return the initial object
  if (initialObj && Object.keys(mergeObj).length === 0) {
    return initialObj;
  }

  // Clone object
  const output = { ...initialObj };

  // Merge values into output, resursively
  for (const key in mergeObj) {
    if (Object.prototype.hasOwnProperty.call(mergeObj, key)) {
      output[key] = merge(output[key], mergeObj[key], levels - 1);
    }
  }

  return output;
}

const SCOPE_SPAN_FIELD = '_sentrySpan';

/**
 * Set the active span for a given scope.
 * NOTE: This should NOT be used directly, but is only used internally by the trace methods.
 */
function _setSpanForScope(scope, span) {
  if (span) {
    addNonEnumerableProperty(scope , SCOPE_SPAN_FIELD, span);
  } else {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete (scope )[SCOPE_SPAN_FIELD];
  }
}

/**
 * Get the active span for a given scope.
 * NOTE: This should NOT be used directly, but is only used internally by the trace methods.
 */
function _getSpanForScope(scope) {
  return scope[SCOPE_SPAN_FIELD];
}

/**
 * Default value for maximum number of breadcrumbs added to an event.
 */
const DEFAULT_MAX_BREADCRUMBS = 100;

/**
 * A context to be used for capturing an event.
 * This can either be a Scope, or a partial ScopeContext,
 * or a callback that receives the current scope and returns a new scope to use.
 */

/**
 * Holds additional event information.
 */
class Scope {
  /** Flag if notifying is happening. */

  /** Callback for client to receive scope changes. */

  /** Callback list that will be called during event processing. */

  /** Array of breadcrumbs. */

  /** User */

  /** Tags */

  /** Extra */

  /** Contexts */

  /** Attachments */

  /** Propagation Context for distributed tracing */

  /**
   * A place to stash data which is needed at some point in the SDK's event processing pipeline but which shouldn't get
   * sent to Sentry
   */

  /** Fingerprint */

  /** Severity */

  /**
   * Transaction Name
   *
   * IMPORTANT: The transaction name on the scope has nothing to do with root spans/transaction objects.
   * It's purpose is to assign a transaction to the scope that's added to non-transaction events.
   */

  /** Session */

  /** The client on this scope */

  /** Contains the last event id of a captured event.  */

  // NOTE: Any field which gets added here should get added not only to the constructor but also to the `clone` method.

   constructor() {
    this._notifyingListeners = false;
    this._scopeListeners = [];
    this._eventProcessors = [];
    this._breadcrumbs = [];
    this._attachments = [];
    this._user = {};
    this._tags = {};
    this._extra = {};
    this._contexts = {};
    this._sdkProcessingMetadata = {};
    this._propagationContext = {
      traceId: generateTraceId(),
      sampleRand: Math.random(),
    };
  }

  /**
   * Clone all data from this scope into a new scope.
   */
   clone() {
    const newScope = new Scope();
    newScope._breadcrumbs = [...this._breadcrumbs];
    newScope._tags = { ...this._tags };
    newScope._extra = { ...this._extra };
    newScope._contexts = { ...this._contexts };
    if (this._contexts.flags) {
      // We need to copy the `values` array so insertions on a cloned scope
      // won't affect the original array.
      newScope._contexts.flags = {
        values: [...this._contexts.flags.values],
      };
    }

    newScope._user = this._user;
    newScope._level = this._level;
    newScope._session = this._session;
    newScope._transactionName = this._transactionName;
    newScope._fingerprint = this._fingerprint;
    newScope._eventProcessors = [...this._eventProcessors];
    newScope._attachments = [...this._attachments];
    newScope._sdkProcessingMetadata = { ...this._sdkProcessingMetadata };
    newScope._propagationContext = { ...this._propagationContext };
    newScope._client = this._client;
    newScope._lastEventId = this._lastEventId;

    _setSpanForScope(newScope, _getSpanForScope(this));

    return newScope;
  }

  /**
   * Update the client assigned to this scope.
   * Note that not every scope will have a client assigned - isolation scopes & the global scope will generally not have a client,
   * as well as manually created scopes.
   */
   setClient(client) {
    this._client = client;
  }

  /**
   * Set the ID of the last captured error event.
   * This is generally only captured on the isolation scope.
   */
   setLastEventId(lastEventId) {
    this._lastEventId = lastEventId;
  }

  /**
   * Get the client assigned to this scope.
   */
   getClient() {
    return this._client ;
  }

  /**
   * Get the ID of the last captured error event.
   * This is generally only available on the isolation scope.
   */
   lastEventId() {
    return this._lastEventId;
  }

  /**
   * @inheritDoc
   */
   addScopeListener(callback) {
    this._scopeListeners.push(callback);
  }

  /**
   * Add an event processor that will be called before an event is sent.
   */
   addEventProcessor(callback) {
    this._eventProcessors.push(callback);
    return this;
  }

  /**
   * Set the user for this scope.
   * Set to `null` to unset the user.
   */
   setUser(user) {
    // If null is passed we want to unset everything, but still define keys,
    // so that later down in the pipeline any existing values are cleared.
    this._user = user || {
      email: undefined,
      id: undefined,
      ip_address: undefined,
      username: undefined,
    };

    if (this._session) {
      updateSession(this._session, { user });
    }

    this._notifyScopeListeners();
    return this;
  }

  /**
   * Get the user from this scope.
   */
   getUser() {
    return this._user;
  }

  /**
   * Set an object that will be merged into existing tags on the scope,
   * and will be sent as tags data with the event.
   */
   setTags(tags) {
    this._tags = {
      ...this._tags,
      ...tags,
    };
    this._notifyScopeListeners();
    return this;
  }

  /**
   * Set a single tag that will be sent as tags data with the event.
   */
   setTag(key, value) {
    this._tags = { ...this._tags, [key]: value };
    this._notifyScopeListeners();
    return this;
  }

  /**
   * Set an object that will be merged into existing extra on the scope,
   * and will be sent as extra data with the event.
   */
   setExtras(extras) {
    this._extra = {
      ...this._extra,
      ...extras,
    };
    this._notifyScopeListeners();
    return this;
  }

  /**
   * Set a single key:value extra entry that will be sent as extra data with the event.
   */
   setExtra(key, extra) {
    this._extra = { ...this._extra, [key]: extra };
    this._notifyScopeListeners();
    return this;
  }

  /**
   * Sets the fingerprint on the scope to send with the events.
   * @param {string[]} fingerprint Fingerprint to group events in Sentry.
   */
   setFingerprint(fingerprint) {
    this._fingerprint = fingerprint;
    this._notifyScopeListeners();
    return this;
  }

  /**
   * Sets the level on the scope for future events.
   */
   setLevel(level) {
    this._level = level;
    this._notifyScopeListeners();
    return this;
  }

  /**
   * Sets the transaction name on the scope so that the name of e.g. taken server route or
   * the page location is attached to future events.
   *
   * IMPORTANT: Calling this function does NOT change the name of the currently active
   * root span. If you want to change the name of the active root span, use
   * `Sentry.updateSpanName(rootSpan, 'new name')` instead.
   *
   * By default, the SDK updates the scope's transaction name automatically on sensible
   * occasions, such as a page navigation or when handling a new request on the server.
   */
   setTransactionName(name) {
    this._transactionName = name;
    this._notifyScopeListeners();
    return this;
  }

  /**
   * Sets context data with the given name.
   * Data passed as context will be normalized. You can also pass `null` to unset the context.
   * Note that context data will not be merged - calling `setContext` will overwrite an existing context with the same key.
   */
   setContext(key, context) {
    if (context === null) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this._contexts[key];
    } else {
      this._contexts[key] = context;
    }

    this._notifyScopeListeners();
    return this;
  }

  /**
   * Set the session for the scope.
   */
   setSession(session) {
    if (!session) {
      delete this._session;
    } else {
      this._session = session;
    }
    this._notifyScopeListeners();
    return this;
  }

  /**
   * Get the session from the scope.
   */
   getSession() {
    return this._session;
  }

  /**
   * Updates the scope with provided data. Can work in three variations:
   * - plain object containing updatable attributes
   * - Scope instance that'll extract the attributes from
   * - callback function that'll receive the current scope as an argument and allow for modifications
   */
   update(captureContext) {
    if (!captureContext) {
      return this;
    }

    const scopeToMerge = typeof captureContext === 'function' ? captureContext(this) : captureContext;

    const scopeInstance =
      scopeToMerge instanceof Scope
        ? scopeToMerge.getScopeData()
        : isPlainObject(scopeToMerge)
          ? (captureContext )
          : undefined;

    const { tags, extra, user, contexts, level, fingerprint = [], propagationContext } = scopeInstance || {};

    this._tags = { ...this._tags, ...tags };
    this._extra = { ...this._extra, ...extra };
    this._contexts = { ...this._contexts, ...contexts };

    if (user && Object.keys(user).length) {
      this._user = user;
    }

    if (level) {
      this._level = level;
    }

    if (fingerprint.length) {
      this._fingerprint = fingerprint;
    }

    if (propagationContext) {
      this._propagationContext = propagationContext;
    }

    return this;
  }

  /**
   * Clears the current scope and resets its properties.
   * Note: The client will not be cleared.
   */
   clear() {
    // client is not cleared here on purpose!
    this._breadcrumbs = [];
    this._tags = {};
    this._extra = {};
    this._user = {};
    this._contexts = {};
    this._level = undefined;
    this._transactionName = undefined;
    this._fingerprint = undefined;
    this._session = undefined;
    _setSpanForScope(this, undefined);
    this._attachments = [];
    this.setPropagationContext({ traceId: generateTraceId(), sampleRand: Math.random() });

    this._notifyScopeListeners();
    return this;
  }

  /**
   * Adds a breadcrumb to the scope.
   * By default, the last 100 breadcrumbs are kept.
   */
   addBreadcrumb(breadcrumb, maxBreadcrumbs) {
    const maxCrumbs = typeof maxBreadcrumbs === 'number' ? maxBreadcrumbs : DEFAULT_MAX_BREADCRUMBS;

    // No data has been changed, so don't notify scope listeners
    if (maxCrumbs <= 0) {
      return this;
    }

    const mergedBreadcrumb = {
      timestamp: dateTimestampInSeconds(),
      ...breadcrumb,
      // Breadcrumb messages can theoretically be infinitely large and they're held in memory so we truncate them not to leak (too much) memory
      message: breadcrumb.message ? truncate(breadcrumb.message, 2048) : breadcrumb.message,
    };

    this._breadcrumbs.push(mergedBreadcrumb);
    if (this._breadcrumbs.length > maxCrumbs) {
      this._breadcrumbs = this._breadcrumbs.slice(-maxCrumbs);
      this._client?.recordDroppedEvent('buffer_overflow', 'log_item');
    }

    this._notifyScopeListeners();

    return this;
  }

  /**
   * Get the last breadcrumb of the scope.
   */
   getLastBreadcrumb() {
    return this._breadcrumbs[this._breadcrumbs.length - 1];
  }

  /**
   * Clear all breadcrumbs from the scope.
   */
   clearBreadcrumbs() {
    this._breadcrumbs = [];
    this._notifyScopeListeners();
    return this;
  }

  /**
   * Add an attachment to the scope.
   */
   addAttachment(attachment) {
    this._attachments.push(attachment);
    return this;
  }

  /**
   * Clear all attachments from the scope.
   */
   clearAttachments() {
    this._attachments = [];
    return this;
  }

  /**
   * Get the data of this scope, which should be applied to an event during processing.
   */
   getScopeData() {
    return {
      breadcrumbs: this._breadcrumbs,
      attachments: this._attachments,
      contexts: this._contexts,
      tags: this._tags,
      extra: this._extra,
      user: this._user,
      level: this._level,
      fingerprint: this._fingerprint || [],
      eventProcessors: this._eventProcessors,
      propagationContext: this._propagationContext,
      sdkProcessingMetadata: this._sdkProcessingMetadata,
      transactionName: this._transactionName,
      span: _getSpanForScope(this),
    };
  }

  /**
   * Add data which will be accessible during event processing but won't get sent to Sentry.
   */
   setSDKProcessingMetadata(newData) {
    this._sdkProcessingMetadata = merge(this._sdkProcessingMetadata, newData, 2);
    return this;
  }

  /**
   * Add propagation context to the scope, used for distributed tracing
   */
   setPropagationContext(context) {
    this._propagationContext = context;
    return this;
  }

  /**
   * Get propagation context from the scope, used for distributed tracing
   */
   getPropagationContext() {
    return this._propagationContext;
  }

  /**
   * Capture an exception for this scope.
   *
   * @returns {string} The id of the captured Sentry event.
   */
   captureException(exception, hint) {
    const eventId = hint?.event_id || uuid4();

    if (!this._client) {
      logger$1.warn('No client configured on scope - will not capture exception!');
      return eventId;
    }

    const syntheticException = new Error('Sentry syntheticException');

    this._client.captureException(
      exception,
      {
        originalException: exception,
        syntheticException,
        ...hint,
        event_id: eventId,
      },
      this,
    );

    return eventId;
  }

  /**
   * Capture a message for this scope.
   *
   * @returns {string} The id of the captured message.
   */
   captureMessage(message, level, hint) {
    const eventId = hint?.event_id || uuid4();

    if (!this._client) {
      logger$1.warn('No client configured on scope - will not capture message!');
      return eventId;
    }

    const syntheticException = new Error(message);

    this._client.captureMessage(
      message,
      level,
      {
        originalException: message,
        syntheticException,
        ...hint,
        event_id: eventId,
      },
      this,
    );

    return eventId;
  }

  /**
   * Capture a Sentry event for this scope.
   *
   * @returns {string} The id of the captured event.
   */
   captureEvent(event, hint) {
    const eventId = hint?.event_id || uuid4();

    if (!this._client) {
      logger$1.warn('No client configured on scope - will not capture event!');
      return eventId;
    }

    this._client.captureEvent(event, { ...hint, event_id: eventId }, this);

    return eventId;
  }

  /**
   * This will be called on every set call.
   */
   _notifyScopeListeners() {
    // We need this check for this._notifyingListeners to be able to work on scope during updates
    // If this check is not here we'll produce endless recursion when something is done with the scope
    // during the callback.
    if (!this._notifyingListeners) {
      this._notifyingListeners = true;
      this._scopeListeners.forEach(callback => {
        callback(this);
      });
      this._notifyingListeners = false;
    }
  }
}

/** Get the default current scope. */
function getDefaultCurrentScope() {
  return getGlobalSingleton('defaultCurrentScope', () => new Scope());
}

/** Get the default isolation scope. */
function getDefaultIsolationScope() {
  return getGlobalSingleton('defaultIsolationScope', () => new Scope());
}

/**
 * This is an object that holds a stack of scopes.
 */
class AsyncContextStack {

   constructor(scope, isolationScope) {
    let assignedScope;
    if (!scope) {
      assignedScope = new Scope();
    } else {
      assignedScope = scope;
    }

    let assignedIsolationScope;
    if (!isolationScope) {
      assignedIsolationScope = new Scope();
    } else {
      assignedIsolationScope = isolationScope;
    }

    // scope stack for domains or the process
    this._stack = [{ scope: assignedScope }];
    this._isolationScope = assignedIsolationScope;
  }

  /**
   * Fork a scope for the stack.
   */
   withScope(callback) {
    const scope = this._pushScope();

    let maybePromiseResult;
    try {
      maybePromiseResult = callback(scope);
    } catch (e) {
      this._popScope();
      throw e;
    }

    if (isThenable(maybePromiseResult)) {
      // @ts-expect-error - isThenable returns the wrong type
      return maybePromiseResult.then(
        res => {
          this._popScope();
          return res;
        },
        e => {
          this._popScope();
          throw e;
        },
      );
    }

    this._popScope();
    return maybePromiseResult;
  }

  /**
   * Get the client of the stack.
   */
   getClient() {
    return this.getStackTop().client ;
  }

  /**
   * Returns the scope of the top stack.
   */
   getScope() {
    return this.getStackTop().scope;
  }

  /**
   * Get the isolation scope for the stack.
   */
   getIsolationScope() {
    return this._isolationScope;
  }

  /**
   * Returns the topmost scope layer in the order domain > local > process.
   */
   getStackTop() {
    return this._stack[this._stack.length - 1] ;
  }

  /**
   * Push a scope to the stack.
   */
   _pushScope() {
    // We want to clone the content of prev scope
    const scope = this.getScope().clone();
    this._stack.push({
      client: this.getClient(),
      scope,
    });
    return scope;
  }

  /**
   * Pop a scope from the stack.
   */
   _popScope() {
    if (this._stack.length <= 1) return false;
    return !!this._stack.pop();
  }
}

/**
 * Get the global async context stack.
 * This will be removed during the v8 cycle and is only here to make migration easier.
 */
function getAsyncContextStack() {
  const registry = getMainCarrier();
  const sentry = getSentryCarrier(registry);

  return (sentry.stack = sentry.stack || new AsyncContextStack(getDefaultCurrentScope(), getDefaultIsolationScope()));
}

function withScope(callback) {
  return getAsyncContextStack().withScope(callback);
}

function withSetScope(scope, callback) {
  const stack = getAsyncContextStack() ;
  return stack.withScope(() => {
    stack.getStackTop().scope = scope;
    return callback(scope);
  });
}

function withIsolationScope(callback) {
  return getAsyncContextStack().withScope(() => {
    return callback(getAsyncContextStack().getIsolationScope());
  });
}

/**
 * Get the stack-based async context strategy.
 */
function getStackAsyncContextStrategy() {
  return {
    withIsolationScope,
    withScope,
    withSetScope,
    withSetIsolationScope: (_isolationScope, callback) => {
      return withIsolationScope(callback);
    },
    getCurrentScope: () => getAsyncContextStack().getScope(),
    getIsolationScope: () => getAsyncContextStack().getIsolationScope(),
  };
}

/**
 * Get the current async context strategy.
 * If none has been setup, the default will be used.
 */
function getAsyncContextStrategy(carrier) {
  const sentry = getSentryCarrier(carrier);

  if (sentry.acs) {
    return sentry.acs;
  }

  // Otherwise, use the default one (stack)
  return getStackAsyncContextStrategy();
}

/**
 * Get the currently active scope.
 */
function getCurrentScope() {
  const carrier = getMainCarrier();
  const acs = getAsyncContextStrategy(carrier);
  return acs.getCurrentScope();
}

/**
 * Captures a message event and sends it to Sentry.
 *
 * @param message The message to send to Sentry.
 * @param captureContext Define the level of the message or pass in additional data to attach to the message.
 * @returns the id of the captured message.
 */
function captureMessage(message, captureContext) {
  // This is necessary to provide explicit scopes upgrade, without changing the original
  // arity of the `captureMessage(message, level)` method.
  const level = typeof captureContext === 'string' ? captureContext : undefined;
  const context = typeof captureContext !== 'string' ? { captureContext } : undefined;
  return getCurrentScope().captureMessage(message, level, context);
}

const PP_SHOP_LIB_VERSION = "1.0.4";

let logProvider = [];
let openObserveInitialized = false;
let openObserveRumInitialized = false;
let openObserveClientId = null;
let openObserveClientIdGetter = null;

function getClientIdFromRuntime() {
    if (typeof openObserveClientIdGetter != 'function') return null

    try {
        const clientId = openObserveClientIdGetter();
        if (typeof clientId == 'string' && clientId) return clientId
    } catch (e) {
        // Ignore store access errors in telemetry context enrichment.
    }

    return null
}

function syncOpenObserveClientContext() {
    const clientId = getClientIdFromRuntime();
    if (!clientId || clientId == openObserveClientId) return

    if (openObserveInitialized) {
        openobserveLogs.setGlobalContextProperty('client_id', clientId);
        openobserveLogs.setUser({ id: clientId });
    }

    if (openObserveRumInitialized && typeof openobserveRum?.setGlobalContextProperty === 'function') {
        openobserveRum.setGlobalContextProperty('client_id', clientId);
        openobserveRum.setUser({ id: clientId });
    }

    openObserveClientId = clientId;
}

function buildOpenObserveContext(args) {
    const clientId = openObserveClientId || getClientIdFromRuntime();

    if (args && typeof args == 'object' && !Array.isArray(args)) {
        const normalizedArgs = { ...args };
        if (typeof normalizedArgs.clientId != 'undefined') {
            if (typeof normalizedArgs.client_id == 'undefined') {
                normalizedArgs.client_id = normalizedArgs.clientId;
            }
            delete normalizedArgs.clientId;
        }

        return clientId
            ? { ...normalizedArgs, client_id: clientId }
            : normalizedArgs
    }

    const context = {};
    if (typeof args !== 'undefined') {
        context.payload = args;
    }
    if (clientId) {
        context.client_id = clientId;
    }

    return context
}

function isOpenObserveIntakeUrl(url) {
    if (typeof url != 'string') return false

    // Local dev proxy path and direct intake path patterns.
    return url.includes('/openobserve/rum/') || url.includes('/rum/v')
}

function shouldDropOpenObserveSelfEvent(event) {
    const httpUrl = event?.http?.url;
    const resourceUrl = event?.resource?.url;
    const message = event?.message;

    if (isOpenObserveIntakeUrl(httpUrl)) return true
    if (isOpenObserveIntakeUrl(resourceUrl)) return true
    if (typeof message == 'string' && message.includes('/openobserve/rum/')) return true

    return false
}

function initOpenObserve(providerList) {
    if (openObserveInitialized) return

    const openObserveProvider = providerList.find((item) => item.provider == "openObserve");
    if (!openObserveProvider?.options) return

    const options = openObserveProvider.options;
    openobserveLogs.init({
        clientToken: options.clientToken,
        site: options.site,
        proxy: options.proxy,
        organizationIdentifier: options.organizationIdentifier,
        service: options.service,
        env: options.env,
        version: PP_SHOP_LIB_VERSION,
        beforeSend: (event) => !shouldDropOpenObserveSelfEvent(event),
        forwardErrorsToLogs: options.forwardErrorsToLogs ?? true,
        insecureHTTP: options.insecureHTTP,
        apiVersion: options.apiVersion,
    });

    
    openObserveInitialized = true;
    syncOpenObserveClientContext();
}

function initOpenObserveRum(providerList) {
    if (openObserveRumInitialized) return

    // RUM may be externalized in some builds (production with external config)
    if (typeof openobserveRum?.init !== 'function') return

    const openObserveProvider = providerList.find((item) => item.provider == "openObserve");
    if (!openObserveProvider?.options) return

    const options = openObserveProvider.options;
    if (!options.applicationId) return

    openobserveRum.init({
        applicationId: options.applicationId,
        clientToken: options.clientToken,
        site: options.site,
        proxy: options.proxy,
        organizationIdentifier: options.organizationIdentifier,
        service: options.service,
        env: options.env,
        version: PP_SHOP_LIB_VERSION,
        beforeSend: (event) => !shouldDropOpenObserveSelfEvent(event),
        insecureHTTP: options.insecureHTTP,
        apiVersion: options.apiVersion,
        trackResources: false,
        trackLongTasks: true,
        trackUserInteractions: true,
        defaultPrivacyLevel: 'mask', // 'allow' or 'mask-user-input' or 'mask'. Use one of the 3 values.
        sessionSampleRate: options.sessionSampleRate ?? 100, // Track 100% of sessions
        sessionReplaySampleRate: options.sessionReplaySampleRate ?? 50, // Record 50% of sessions
        startSessionReplayRecordingManually: true
    });

    if (options.enableSessionReplay) {
        openobserveRum.startSessionReplayRecording();
    }

    openObserveRumInitialized = true;
    syncOpenObserveClientContext();
}

const logger = {
    setClientIdGetter(getter) {
        openObserveClientIdGetter = typeof getter == 'function' ? getter : null;
        syncOpenObserveClientContext();
    },
    setProviders(providers) {
        if (!Array.isArray(providers)) return
        logProvider = providers;
        initOpenObserve(logProvider);
        initOpenObserveRum(logProvider);
    },
    debug: log("debug"),
    error: log("error"),
    info: log("info"),
    warn: log("warn")
};

function log(level) {

    return (message, args) => {
        // Keep client_id context up to date for every emitted event.
        syncOpenObserveClientContext();

        logProvider.forEach((provider) => {
            if (provider[level] && provider.provider == "sentry") {
                captureMessage(message, level);
            } else if (provider[level] && provider.provider == "console") {
                if (typeof args !== 'undefined') {
                    console[level](message, args);
                } else {
                    console[level](message);
                }
            } else if (provider[level] && provider.provider == "openObserve") {  
                openobserveLogs.logger[level](message, buildOpenObserveContext(args));
            }});
    }
}

glob("logger",logger);

const FOLDER_ICON_PRIMARY_COLOR = '#FAAF4D';
const FOLDER_ICON_SECONDARY_COLOR = '#e18907';

/**
 * @module helpers/folderIcons
 * @name Folder Icons
 * @description Fallback SVGs für Folder-Icons (base64 und data-URI)
 * Wird verwendet, wenn kein CDN konfiguriert oder verfügbar ist.
 */

function applyFolderThemeColors(svg) {
  return svg
    .replaceAll('%23FFA000', FOLDER_ICON_SECONDARY_COLOR.replaceAll('#', '%23'))
    .replaceAll('%23FFCA28', FOLDER_ICON_PRIMARY_COLOR.replaceAll('#', '%23'))
}
/**
 * Basis-Folder-Icon SVG (braun, Ordner-Form)
 * Data-URI URL-encoded für direkte Verwendung in background-image
 */
const FOLDER_DEFAULT_SVG = applyFolderThemeColors("data:image/svg+xml,%3Csvg version='1.0' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48' enable-background='new 0 0 48 48'%3E%3Cpath fill='%23FFA000' d='M40,12H22l-4-4H8c-2.2,0-4,1.8-4,4v8h40v-4C44,13.8,42.2,12,40,12z'/%3E%3Cpath fill='%23FFCA28' d='M40,12H8c-2.2,0-4,1.8-4,4v20c0,2.2,1.8,4,4,4h32c2.2,0,4-1.8,4-4V16C44,13.8,42.2,12,40,12z'/%3E%3C/svg%3E");

/**
 * Folder mit Kamera-Icon (oid=1)
 */
const FOLDER_CAMERA_SVG = "data:image/svg+xml,%3Csvg version='1.0' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48' enable-background='new 0 0 48 48'%3E%3Cpath fill='%23FFA000' d='M40,12H22l-4-4H8c-2.2,0-4,1.8-4,4v8h40v-4C44,13.8,42.2,12,40,12z'/%3E%3Cpath fill='%23FFCA28' d='M40,12H8c-2.2,0-4,1.8-4,4v20c0,2.2,1.8,4,4,4h32c2.2,0,4-1.8,4-4V16C44,13.8,42.2,12,40,12z'/%3E%3Cg transform='translate(15,15)'%3E%3Cpath transform='scale(0.04)' d='M0 84V56c0-13.3 10.7-24 24-24h112l9.4-18.7c4-8.2 12.3-13.3 21.4-13.3h114.3c9.1 0 17.4 5.1 21.5 13.3L312 32h112c13.3 0 24 10.7 24 24v28c0 6.6-5.4 12-12 12H12C5.4 96 0 90.6 0 84zm416 56v324c0 26.5-21.5 48-48 48H80c-26.5 0-48-21.5-48-48V140c0-6.6 5.4-12 12-12h360c6.6 0 12 5.4 12 12zm-272 68c0-8.8-7.2-16-16-16s-16 7.2-16 16v224c0 8.8 7.2 16 16 16s16-7.2 16-16V208zm96 0c0-8.8-7.2-16-16-16s-16 7.2-16 16v224c0 8.8 7.2 16 16 16s16-7.2 16-16V208zm96 0c0-8.8-7.2-16-16-16s-16 7.2-16 16v224c0 8.8 7.2 16 16 16s16-7.2 16-16V208z'/%3E%3C/g%3E%3C/svg%3E";

/**
 * Folder mit Kalender/Uhren-Icons (oid=11)
 * Raw SVG mit komplexem Inhalt
 */
const FOLDER_CALENDAR_SVG = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%20standalone%3D%22no%22%3F%3E%3Csvg%20%20%20version%3D%221.0%22%20%20%20viewBox%3D%220%200%2048%2048%22%20%20%20enable-background%3D%22new%200%200%2048%2048%22%20%20%20id%3D%22svg109%22%20%20%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20%20%20xmlns%3Asvg%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cdefs%20%20%20%20%20id%3D%22defs113%22%3E%3CclipPath%20%20%20%20%20%20%20clipPathUnits%3D%22userSpaceOnUse%22%20%20%20%20%20%20%20id%3D%22clipPath2621%22%3E%3Cg%20%20%20%20%20%20%20%20%20id%3D%22use2623%22%20%20%20%20%20%20%20%20%20style%3D%22fill%3A%23000000%3Bfill-opacity%3A1%22%3E%3Cpath%20%20%20%20%20%20%20%20%20%20%20d%3D%22m%2033.986439%2C21.242662%20v%2012.504326%20c%200%2C1.104569%20-0.895431%2C2%20-2%2C2%20h%20-15.98316%20c%20-1.104569%2C0%20-2%2C-0.895431%20-2%2C-2%20V%2021.242662%20m%200%2C-0.617793%20v%20-2.279412%20c%200%2C-1.104569%200.895431%2C-2%202%2C-2%20h%2015.98316%20c%201.104569%2C0%202%2C0.895431%202%2C2%20v%202.279412%22%20%20%20%20%20%20%20%20%20%20%20id%3D%22path98726%22%20%20%20%20%20%20%20%20%20%20%20style%3D%22fill%3A%23000000%3Bfill-opacity%3A1%22%20%2F%3E%3C%2Fg%3E%3C%2FclipPath%3E%3C%2Fdefs%3E%3Cpath%20%20%20%20%20fill%3D%22%23FFA000%22%20%20%20%20%20d%3D%22M40%2C12H22l-4-4H8c-2.2%2C0-4%2C1.8-4%2C4v8h40v-4C44%2C13.8%2C42.2%2C12%2C40%2C12z%22%20%20%20%20%20id%3D%22path105%22%20%2F%3E%3Cpath%20%20%20%20%20fill%3D%22%23FFCA28%22%20%20%20%20%20d%3D%22M40%2C12H8c-2.2%2C0-4%2C1.8-4%2C4v20c0%2C2.2%2C1.8%2C4%2C4%2C4h32c2.2%2C0%2C4-1.8%2C4-4V16C44%2C13.8%2C42.2%2C12%2C40%2C12z%22%20%20%20%20%20id%3D%22path107%22%20%20%20%20%20style%3D%22fill%3A%23FFCA28%3Bfill-opacity%3A1%22%20%2F%3E%3Cpath%20%20%20%20%20d%3D%22m%2033.960589%2C21.257828%20v%2012.504326%20c%200%2C1.104569%20-0.895431%2C2%20-2%2C2%20h%20-15.98316%20c%20-1.104569%2C0%20-2%2C-0.895431%20-2%2C-2%20V%2021.257828%20m%200%2C-0.617793%20v%20-2.279412%20c%200%2C-1.104569%200.895431%2C-2%202%2C-2%20h%2015.98316%20c%201.104569%2C0%202%2C0.895431%202%2C2%20v%202.279412%22%20%20%20%20%20id%3D%22path6045%22%20%20%20%20%20style%3D%22fill%3A%23000000%3Bfill-opacity%3A1%22%20%2F%3E%3Cg%20%20%20%20%20id%3D%22g34352%22%20%20%20%20%20transform%3D%22translate(-0.00230897%2C0.48504616)%22%3E%3Cpath%20%20%20%20%20%20%20style%3D%22fill%3A%23FFCA28%3Bfill-opacity%3A1%3Bstroke%3Anone%3Bstroke-width%3A0.022%3Bstroke-miterlimit%3A4%3Bstroke-dasharray%3Anone%22%20%20%20%20%20%20%20d%3D%22m%2016.28465%2C18.22199%20c%20-0.250472%2C-0.116635%20-0.365768%2C-0.37387%20-0.287794%2C-0.642092%200.09245%2C-0.318009%200.462019%2C-0.476883%200.756749%2C-0.325317%200.234111%2C0.120393%200.344831%2C0.39056%200.267425%2C0.652544%20-0.03586%2C0.121376%20-0.218917%2C0.304432%20-0.340293%2C0.340293%20-0.138437%2C0.0409%20-0.272065%2C0.03232%20-0.396087%2C-0.02543%20z%22%20%20%20%20%20%20%20id%3D%22path32735%22%20%2F%3E%3Cpath%20%20%20%20%20%20%20style%3D%22fill%3A%23FFCA28%3Bfill-opacity%3A1%3Bstroke%3Anone%3Bstroke-width%3A0.022%3Bstroke-miterlimit%3A4%3Bstroke-dasharray%3Anone%22%20%20%20%20%20%20%20d%3D%22m%2018.740901%2C18.212564%20c%20-0.250472%2C-0.116635%20-0.365768%2C-0.37387%20-0.287794%2C-0.642092%200.09245%2C-0.318009%200.462019%2C-0.476883%200.756749%2C-0.325317%200.234111%2C0.120393%200.344831%2C0.39056%200.267425%2C0.652544%20-0.03586%2C0.121376%20-0.218917%2C0.304432%20-0.340293%2C0.340293%20-0.138437%2C0.0409%20-0.272065%2C0.03232%20-0.396087%2C-0.02543%20z%22%20%20%20%20%20%20%20id%3D%22path32735-3%22%20%2F%3E%3Cpath%20%20%20%20%20%20%20style%3D%22fill%3A%23FFCA28%3Bfill-opacity%3A1%3Bstroke%3Anone%3Bstroke-width%3A0.022%3Bstroke-miterlimit%3A4%3Bstroke-dasharray%3Anone%22%20%20%20%20%20%20%20d%3D%22m%2021.24919%2C18.17937%20c%20-0.250472%2C-0.116635%20-0.365768%2C-0.37387%20-0.287794%2C-0.642092%200.09245%2C-0.318009%200.462019%2C-0.476883%200.756749%2C-0.325317%200.234111%2C0.120393%200.344831%2C0.39056%200.267425%2C0.652544%20-0.03586%2C0.121376%20-0.218917%2C0.304432%20-0.340293%2C0.340293%20-0.138437%2C0.0409%20-0.272065%2C0.03232%20-0.396087%2C-0.02543%20z%22%20%20%20%20%20%20%20id%3D%22path32735-3-6%22%20%2F%3E%3Cpath%20%20%20%20%20%20%20style%3D%22fill%3A%23FFCA28%3Bfill-opacity%3A1%3Bstroke%3Anone%3Bstroke-width%3A0.022%3Bstroke-miterlimit%3A4%3Bstroke-dasharray%3Anone%22%20%20%20%20%20%20%20d%3D%22m%2023.789775%2C18.191984%20c%20-0.250472%2C-0.116635%20-0.365768%2C-0.37387%20-0.287794%2C-0.642092%200.09245%2C-0.318009%200.462019%2C-0.476883%200.756749%2C-0.325317%200.234111%2C0.120393%200.344831%2C0.39056%200.267425%2C0.652544%20-0.03586%2C0.121376%20-0.218917%2C0.304432%20-0.340293%2C0.340293%20-0.138437%2C0.0409%20-0.272065%2C0.03232%20-0.396087%2C-0.02543%20z%22%20%20%20%20%20%20%20id%3D%22path32735-3-6-7%22%20%2F%3E%3Cpath%20%20%20%20%20%20%20style%3D%22fill%3A%23FFCA28%3Bfill-opacity%3A1%3Bstroke%3Anone%3Bstroke-width%3A0.022%3Bstroke-miterlimit%3A4%3Bstroke-dasharray%3Anone%22%20%20%20%20%20%20%20d%3D%22m%2026.253012%2C18.195132%20c%20-0.250472%2C-0.116635%20-0.365768%2C-0.37387%20-0.287794%2C-0.642092%200.09245%2C-0.318009%200.462019%2C-0.476883%200.756749%2C-0.325317%200.234111%2C0.120393%200.344831%2C0.39056%200.267425%2C0.652544%20-0.03586%2C0.121376%20-0.218917%2C0.304432%20-0.340293%2C0.340293%20-0.138437%2C0.0409%20-0.272065%2C0.03232%20-0.396087%2C-0.02543%20z%22%20%20%20%20%20%20%20id%3D%22path32735-3-6-7-5%22%20%2F%3E%3Cpath%20%20%20%20%20%20%20style%3D%22fill%3A%23FFCA28%3Bfill-opacity%3A1%3Bstroke%3Anone%3Bstroke-width%3A0.022%3Bstroke-miterlimit%3A4%3Bstroke-dasharray%3Anone%22%20%20%20%20%20%20%20d%3D%22m%2028.752573%2C18.183445%20c%20-0.250472%2C-0.116635%20-0.365768%2C-0.37387%20-0.287794%2C-0.642092%200.09245%2C-0.318009%200.462019%2C-0.476883%200.756749%2C-0.325317%200.234111%2C0.120393%200.344831%2C0.39056%200.267425%2C0.652544%20-0.03586%2C0.121376%20-0.218917%2C0.304432%20-0.340293%2C0.340293%20-0.138437%2C0.0409%20-0.272065%2C0.03232%20-0.396087%2C-0.02543%20z%22%20%20%20%20%20%20%20id%3D%22path32735-3-6-7-5-3%22%20%2F%3E%3Cpath%20%20%20%20%20%20%20style%3D%22fill%3A%23FFCA28%3Bfill-opacity%3A1%3Bstroke%3Anone%3Bstroke-width%3A0.022%3Bstroke-miterlimit%3A4%3Bstroke-dasharray%3Anone%22%20%20%20%20%20%20%20d%3D%22m%2031.257113%2C18.211236%20c%20-0.250473%2C-0.116635%20-0.365769%2C-0.37387%20-0.287795%2C-0.642092%200.09245%2C-0.318009%200.46202%2C-0.476883%200.756749%2C-0.325317%200.234111%2C0.120393%200.344831%2C0.39056%200.267425%2C0.652544%20-0.03586%2C0.121376%20-0.218917%2C0.304432%20-0.340293%2C0.340293%20-0.138437%2C0.0409%20-0.272065%2C0.03232%20-0.396086%2C-0.02543%20z%22%20%20%20%20%20%20%20id%3D%22path32735-3-6-7-5-3-5%22%20%2F%3E%3C%2Fg%3E%3Cg%20%20%20%20%20aria-label%3D%2231%22%20%20%20%20%20id%3D%22text48402%22%20%20%20%20%20style%3D%22font-weight%3A600%3Bfont-size%3A12px%3Bline-height%3A1.25%3Bfont-family%3AFreeSans%3B-inkscape-font-specification%3A'FreeSans%20Semi-Bold'%3Bletter-spacing%3A0px%3Bword-spacing%3A0px%3Bfill%3A%23FFCA28%22%3E%3Cpath%20%20%20%20%20%20%20d%3D%22m%2018.63959%2C25.875627%20q%200%2C-0.66%200.18%2C-1.164%200.192%2C-0.504%200.468%2C-0.792%200.288%2C-0.288%200.672%2C-0.456%200.384%2C-0.18%200.72%2C-0.24%200.348%2C-0.06%200.72%2C-0.06%201.236%2C0%201.968%2C0.636%200.732%2C0.624%200.732%2C1.68%200%2C0.588%20-0.264%2C1.02%20-0.264%2C0.42%20-0.852%2C0.792%200.72%2C0.348%201.056%2C0.864%200.336%2C0.504%200.336%2C1.248%200%2C1.248%20-0.816%2C1.992%20-0.804%2C0.732%20-2.16%2C0.732%20-1.308%2C0%20-2.088%2C-0.744%20-0.768%2C-0.756%20-0.78%2C-2.028%20h%201.632%20q%200.072%2C1.332%201.272%2C1.332%200.552%2C0%200.9%2C-0.36%200.36%2C-0.36%200.36%2C-0.924%200%2C-0.468%20-0.252%2C-0.804%20-0.252%2C-0.348%20-0.672%2C-0.468%20-0.336%2C-0.084%20-0.984%2C-0.084%20v%20-1.128%20h%200.144%20q%201.488%2C0%201.488%2C-1.284%200%2C-0.528%20-0.276%2C-0.816%20-0.276%2C-0.3%20-0.78%2C-0.3%20-0.66%2C0%20-0.9%2C0.372%20-0.24%2C0.372%20-0.264%2C1.128%20h%20-1.56%20z%22%20%20%20%20%20%20%20id%3D%22path39%22%20%2F%3E%3Cpath%20%20%20%20%20%20%20d%3D%22m%2027.711587%2C25.983627%20h%20-2.04%20v%20-1.116%20q%202.208%2C0%202.604%2C-1.524%20h%201.116%20v%208.508%20h%20-1.68%20z%22%20%20%20%20%20%20%20id%3D%22path41%22%20%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E";

/**
 * Folder mit Share/Download-Icon (oid=99)
 */
const FOLDER_SHARE_SVG = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%20standalone%3D%22no%22%3F%3E%3Csvg%20%20%20version%3D%221.0%22%20%20%20viewBox%3D%220%200%2048%2048%22%20%20%20enable-background%3D%22new%200%200%2048%2048%22%20%20%20id%3D%22svg6%22%20%20%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20%20%20xmlns%3Asvg%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cdefs%20%20%20%20%20id%3D%22defs10%22%20%2F%3E%3Cpath%20%20%20%20%20fill%3D%22%23FFA000%22%20%20%20%20%20d%3D%22M40%2C12H22l-4-4H8c-2.2%2C0-4%2C1.8-4%2C4v8h40v-4C44%2C13.8%2C42.2%2C12%2C40%2C12z%22%20%20%20%20%20id%3D%22path2%22%20%2F%3E%3Cpath%20%20%20%20%20fill%3D%22%23FFCA28%22%20%20%20%20%20d%3D%22M40%2C12H8c-2.2%2C0-4%2C1.8-4%2C4v20c0%2C2.2%2C1.8%2C4%2C4%2C4h32c2.2%2C0%2C4-1.8%2C4-4V16C44%2C13.8%2C42.2%2C12%2C40%2C12z%22%20%20%20%20%20id%3D%22path4%22%20%2F%3E%3Cg%20%20%20%20%20style%3D%22fill%3A%23000000%22%20%20%20%20%20id%3D%22g83%22%20%20%20%20%20transform%3D%22matrix(0.03883039%2C3.7836594e-4%2C0.0016161%2C0.03881095%2C15.268955%2C16.976112)%22%3E%3Cg%20%20%20%20%20%20%20id%3D%22g73%22%3E%3Cg%20%20%20id%3D%22g71%22%3E%3Cpath%20%20%20d%3D%22m%20339.588%2C314.529%20c%20-14.215%2C0%20-27.456%2C4.133%20-38.621%2C11.239%20l%20-112.682%2C-78.67%20c%201.809%2C-6.315%202.798%2C-12.976%202.798%2C-19.871%200%2C-6.896%20-0.989%2C-13.557%20-2.798%2C-19.871%20l%20109.64%2C-76.547%20c%2011.764%2C8.356%2026.133%2C13.286%2041.662%2C13.286%2039.79%2C0%2072.047%2C-32.257%2072.047%2C-72.047%20C%20411.634%2C32.258%20379.378%2C0%20339.588%2C0%20c%20-39.79%2C0%20-72.047%2C32.257%20-72.047%2C72.047%200%2C5.255%200.578%2C10.373%201.646%2C15.308%20l%20-112.424%2C78.491%20c%20-10.974%2C-6.759%20-23.892%2C-10.666%20-37.727%2C-10.666%20-39.79%2C0%20-72.047%2C32.257%20-72.047%2C72.047%200%2C39.79%2032.256%2C72.047%2072.047%2C72.047%2013.834%2C0%2026.753%2C-3.907%2037.727%2C-10.666%20l%20113.292%2C79.097%20c%20-1.629%2C6.017%20-2.514%2C12.34%20-2.514%2C18.872%200%2C39.79%2032.257%2C72.047%2072.047%2C72.047%2039.79%2C0%2072.047%2C-32.257%2072.047%2C-72.047%200%2C-39.79%20-32.257%2C-72.048%20-72.047%2C-72.048%20z%22%20%20%20id%3D%22path69%22%20%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E";

/**
 * Map von Folder OID zu ihrem SVG-Fallback
 * Wird verwendet wenn CDN nicht verfügbar ist
 */
const FOLDER_SVG_MAP = {
  default: FOLDER_DEFAULT_SVG,
  1: FOLDER_CAMERA_SVG,
  11: FOLDER_CALENDAR_SVG,
  99: FOLDER_SHARE_SVG
};

/**
 * Gibt Fallback-SVG für eine Folder OID zurück
 * @param {number|string} oid - Folder OID
 * @returns {string} Data-URI SVG string
 */
function getFolderSvgFallback(oid) {
  return applyFolderThemeColors(FOLDER_SVG_MAP[oid] || FOLDER_SVG_MAP.default)
}

const UID_CACHE_PREFIX = 'session-file-import-uid-cache:v1';

function normalizeUid(uid) {
    if (uid === null || typeof uid === 'undefined') return null;
    const normalized = `${uid}`.trim();
    return normalized ? normalized : null;
}

function getUidCacheKey(sessionId, kid, resource) {
    return `${UID_CACHE_PREFIX}:${sessionId}:${kid}:${resource}`;
}

function readUidCache(sessionId, kid, resource) {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    try {
        const raw = window.localStorage.getItem(getUidCacheKey(sessionId, kid, resource));
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function writeUidCache(sessionId, kid, resource, entries) {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        const byId = {};
        entries.forEach((entry) => {
            if (!entry) return;
            const id = `${entry.id ?? ''}`.trim();
            const uid = normalizeUid(entry.uid);
            if (!id || !uid) return;
            byId[id] = uid;
        });
        if (Object.keys(byId).length === 0) return;
        const payload = {
            createdAt: Date.now(),
            byId
        };
        window.localStorage.setItem(getUidCacheKey(sessionId, kid, resource), JSON.stringify(payload));
    } catch {
        // Ignore cache write failures.
    }
}

function clearUidCache(sessionId, kid, resource) {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.removeItem(getUidCacheKey(sessionId, kid, resource));
    } catch {
        // Ignore cache cleanup failures.
    }
}

function withCachedUid(sessionFiles, cachedPayload) {
    const byId = cachedPayload?.byId && typeof cachedPayload.byId === 'object'
        ? cachedPayload.byId
        : {};

    return sessionFiles.map((file) => {
        const uid = normalizeUid(file?.uid);
        if (uid) return file;
        const id = `${file?.id ?? ''}`.trim();
        if (!id || !byId[id]) return file;
        return {
            ...file,
            uid: byId[id]
        };
    });
}

function hasRecoveredUidFromCache(loadedSessionFiles, sessionFiles) {
    if (!Array.isArray(loadedSessionFiles) || !Array.isArray(sessionFiles)) return false;

    const loadedUidById = {};
    loadedSessionFiles.forEach((file) => {
        const id = `${file?.id ?? ''}`.trim();
        if (!id) return;
        loadedUidById[id] = normalizeUid(file?.uid);
    });

    return sessionFiles.some((file) => {
        const id = `${file?.id ?? ''}`.trim();
        if (!id) return false;
        const mergedUid = normalizeUid(file?.uid);
        const originalUid = loadedUidById[id];
        return !originalUid && !!mergedUid;
    });
}

function getUidEntries(sessionFiles) {
    return sessionFiles
        .map((file) => {
            const id = `${file?.id ?? ''}`.trim();
            const uid = normalizeUid(file?.uid);
            if (!id || !uid) return null;
            return { id, uid };
        })
        .filter(Boolean);
}

function toFiniteNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function toImportItem(file, apiUrl, sessionId, resource) {
    if (!file || !file.resourceUrl) return null;

    const src = apiUrl + file.resourceUrl
        .replace('{resource}', 'orig')
        .replace('{ref}', sessionId);

    const replacementFor = {
        kid: 0,
        bid: file.bid,
        id: file.id
    };

    const normalizedUid = normalizeUid(file.uid);
    if (normalizedUid) {
        replacementFor.uid = normalizedUid;
    }

    if (resource === 'media' && file.jid !== null && typeof file.jid !== 'undefined' && file.jid !== '') {
        replacementFor.jid = file.jid;
    }

    const item = {
        oid: toFiniteNumber(file.oid),
        name: file.name || '',
        width: toFiniteNumber(file.width),
        height: toFiniteNumber(file.height),
        pages: toFiniteNumber(file.pages),
        src,
        id: file.id,
        action: { replacementFor },
        sessionBid: file.bid
    };

    if (normalizedUid) {
        item.uid = normalizedUid;
    }

    return item;
}

async function importSessionFilesForUser(resource, kid, sessionId, defaultOid) {
    if (!kid || !sessionId) {
        return { imported: false, reason: 'missing-kid-or-session', resource };
    }

    const apiEndpoint = `users/0/session/${sessionId}/${resource}`;
    const rawFiles = await ppapiClient.get(apiEndpoint);
    const loadedSessionFiles = rawFiles?.data?.() || [];
    const cachedUidPayload = readUidCache(sessionId, kid, resource);
    const sessionFiles = withCachedUid(loadedSessionFiles, cachedUidPayload);
    const uidCacheUsed = hasRecoveredUidFromCache(loadedSessionFiles, sessionFiles);

    if (!Array.isArray(sessionFiles) || sessionFiles.length === 0) {
        return { imported: false, reason: `no-session-${resource}`, resource };
    }

    const uidEntries = getUidEntries(sessionFiles);
    if (uidEntries.length > 0) {
        writeUidCache(sessionId, kid, resource, uidEntries);

        const updatePayload = {
            '@update': {
                uidList: uidEntries.map((entry) => entry.uid),
                set: { uid: null }
            }
        };
        await ppapiClient.put(`session/${sessionId}/${resource}`, updatePayload);
    }

    const configStore = useConfigStore();
    const apiUrl = configStore.apiUrl || '';

    const items = sessionFiles.map((file) => toImportItem(file, apiUrl, sessionId, resource)).filter(Boolean);

    if (items.length === 0) {
        return { imported: false, reason: 'no-importable-items', resource };
    }

    const payload = {
        kid,
        items,
        session: sessionId,
        oid: Number.isFinite(Number(defaultOid)) ? Number(defaultOid) : (items[0]?.oid || 0)
    };

    const importResponse = await ppapiClient.post(`users/${resource}/import`, payload);

    const importedFiles = extractImportedEntries(importResponse, resource);

    clearUidCache(sessionId, kid, resource);

    return {
        imported: true,
        count: importedFiles.length || Number(importResponse?.imported) || items.length,
        uidCacheUsed,
        resource,
        response: importResponse
    };
}

async function importSessionImagesForUser(kid, sessionId, defaultOid) {
    return importSessionFilesForUser('images', kid, sessionId, defaultOid);
}

async function importSessionMediaForUser(kid, sessionId, defaultOid) {
    return importSessionFilesForUser('media', kid, sessionId, defaultOid);
}

function extractImportedEntries(response, resource) {
    if (Array.isArray(response?.[resource])) return response[resource];
    if (Array.isArray(response?.items)) return response.items;
    return [];
}

function normalizeImportResult(resource, result, error) {
    if (result) return result;
    return {
        imported: false,
        resource,
        reason: 'import-failed',
        error
    };
}

function getImportedEntries(result, resource) {
    return extractImportedEntries(result?.response, resource);
}

async function runLoginFileImport(authStore, previousUser) {
    const currentKid = authStore?.user?.kid;
    const hadKidBefore = Boolean(previousUser?.kid);

    // Nur beim Wechsel von Session/Nicht-Login zu echtem User-Login importieren.
    if (!currentKid || hadKidBefore) {
        return { imported: false, reason: 'no-login-transition' };
    }

    const sessionId = authStore.getSessionId();
    if (!sessionId) {
        return { imported: false, reason: 'missing-session-id' };
    }

    const importMarker = `${sessionId}:${currentKid}`;
    if (authStore.setup?.lastSessionFileImport === importMarker) {
        return { imported: false, reason: 'already-imported' };
    }

    let imageResult = null;
    let mediaResult = null;
    let imageError = null;
    let mediaError = null;

    try {
        imageResult = await importSessionImagesForUser(currentKid, sessionId, authStore.user?.oid);
    } catch (error) {
        imageError = error;
        logger.warn('[auth.store] session image import failed', error);
    }

    try {
        mediaResult = await importSessionMediaForUser(currentKid, sessionId, authStore.user?.oid);
    } catch (error) {
        mediaError = error;
        logger.warn('[auth.store] session media import failed', error);
    }

    const normalizedImageResult = normalizeImportResult('images', imageResult, imageError);
    const normalizedMediaResult = normalizeImportResult('media', mediaResult, mediaError);

    const totalCount = (Number(normalizedImageResult?.count) || 0) + (Number(normalizedMediaResult?.count) || 0);
    const imported = Boolean(normalizedImageResult?.imported || normalizedMediaResult?.imported);

    const combinedResponse = {
        images: getImportedEntries(normalizedImageResult, 'images'),
        media: getImportedEntries(normalizedMediaResult, 'media'),
        imageResponse: normalizedImageResult?.response || null,
        mediaResponse: normalizedMediaResult?.response || null,
        uidRecovery: {
            images: Boolean(normalizedImageResult?.uidCacheUsed),
            media: Boolean(normalizedMediaResult?.uidCacheUsed),
            used: Boolean(normalizedImageResult?.uidCacheUsed || normalizedMediaResult?.uidCacheUsed)
        }
    };

    if (imported) {
        authStore.setup.lastSessionFileImport = importMarker;
        authStore.setup.lastSessionImageImport = importMarker;
        logger.info(`[auth.store] imported session files for kid ${currentKid} (images: ${normalizedImageResult?.count || 0}, media: ${normalizedMediaResult?.count || 0})`);
    }

    return {
        imported,
        count: totalCount,
        image: normalizedImageResult,
        media: normalizedMediaResult,
        response: combinedResponse
    };
}

const PARAMETER_TTL = 60 * 60 * 1000;

let sessionStore = false;
let parameterLoadedAt = 0;

const parameter = new Proxy({ country: {}, product: {} }, {
    async get(target, prop) {

        if (typeof prop === 'symbol') return target[prop]

// Bugfix: beim Versuch unbekannte Parameter zu laden wird ständig der ppapiClient aufgerufen
        if (!sessionStore) sessionStore = useSessionStore();

        const parameterCacheValid = parameterLoadedAt > 0
            && Date.now() - parameterLoadedAt < PARAMETER_TTL;

        if (parameterCacheValid && typeof sessionStore.parameter?.[prop] !== 'undefined') {
            return sessionStore.parameter[prop]
        }

        if (!parameterCacheValid) {
            try {
                const ppapiPar = await ppapiClient.get("parameter");
                if (ppapiPar && typeof ppapiPar === 'object') {
                    sessionStore.parameter = { ...sessionStore.parameter, ...ppapiPar };
                    parameterLoadedAt = Date.now();
                }
            } catch (error) {
                // Bei Fehlern späteren Retry erlauben und Fallback liefern.
                parameterLoadedAt = 0;
                return sessionStore.parameter?.[prop] ?? target[prop]
            }
        }

        if (typeof sessionStore.parameter?.[prop] !== 'undefined') {
            return sessionStore.parameter[prop]
        }

        return target[prop]

    }
});

const imagePath = import.meta.env.PROD
    ? '/theme/public/assets/images'
    : '/src/assets/images';

const paymentMap = {
    paypal: {
        icon: `${imagePath}/WW PayPal text.svg`,
        createPayment: true,
        method: 'paypal',
        captureMode: 'automatic'
    },
    lastschrift: {
        icon: `${imagePath}/sepa-lastschrift.svg`,
        createPayment: false,
        method: 'directdebit'
    },
    rechnung: {
        icon: `${imagePath}/rechnung.svg`,
        createPayment: false,
        method: 'rechnung'
    },
    vorkasse: {
        icon: `${imagePath}/vorkasse.svg`,
        createPayment: false,
        method: 'banktransfer'
    },
    kreditkarte: {
        icon: `${imagePath}/WW Mastercard+VISA alt.svg`,
        createPayment: true,
        method: 'creditcard',
        captureMode: 'automatic'
    },
    sofort: {
        icon: `${imagePath}/EU Klarna Sofort.svg`,
        createPayment: true,
        method: 'sofort'
    }
};

const CART_PUSH_DEBOUNCE_MS = 1500;
let _queuedCartPushTimer = null;
let _queuedCartPushKid = null;
let _activeCartPushPromise = null;

const useSessionStore = defineStore('session',{
    state: () => ({
        checkout: {
            section: "cart",
            addresses: {},
            bankaccount: null,
            destinationCountry: "DE",
            email: null,
            login: null,
            selectStandardInvoiceAddressAfterLogin: false,
            payment: null,
            checkoutId: null,
            cartItems: []
        },
        addresses: [],
        bankaccounts: [],
        parameter: {},
        cart: {
            id: null,
            serverCartId: null,
            sessionId: null,
            items: []
        },
        route: ""
    }),
    actions: {
        stripPricesFromProduct(product) {
            if (!product || typeof product !== 'object') return
            if (typeof product.prices !== 'undefined') {
                delete product.prices;
            }
        },

        stripPricesFromSelectedOptionPrices(selectedOptionPrices) {
            if (!Array.isArray(selectedOptionPrices)) return
            selectedOptionPrices.forEach((optionPrice) => {
                if (!optionPrice || typeof optionPrice !== 'object') return
                if (typeof optionPrice.prices !== 'undefined') {
                    delete optionPrice.prices;
                }
            });
        },

        sanitizeCartItemPrices(item) {
            if (!item || typeof item !== 'object') return

            this.stripPricesFromProduct(item.product);
            this.stripPricesFromSelectedOptionPrices(item.selectedOptionPrices);

            if (item.selectorState && typeof item.selectorState === 'object') {
                this.stripPricesFromProduct(item.selectorState.product);
                if (item.selectorState.optionPrices && typeof item.selectorState.optionPrices === 'object') {
                    Object.values(item.selectorState.optionPrices).forEach((optionPrice) => {
                        if (!optionPrice || typeof optionPrice !== 'object') return
                        if (typeof optionPrice.prices !== 'undefined') {
                            delete optionPrice.prices;
                        }
                    });
                }
            }
        },

        sanitizeCartItemsPrices(items) {
            if (!Array.isArray(items)) return
            items.forEach((item) => {
                this.sanitizeCartItemPrices(item);
            });
        },

        replaceCartMediaFromImport(importResponse) {
            this.normalizeCart();

            const toLookupKey = (value) => {
                if (typeof value === 'number') return String(value)
                if (typeof value !== 'string') return ''

                const normalized = value.trim();
                return normalized || ''
            };

            const importedFiles = [
                ...(Array.isArray(importResponse?.images) ? importResponse.images : []),
                ...(Array.isArray(importResponse?.media) ? importResponse.media : [])
            ];

            const hasCartItems = Array.isArray(this.cart.items) && this.cart.items.length > 0;
            const hasCheckoutItems = Array.isArray(this.checkout?.cartItems) && this.checkout.cartItems.length > 0;

            if (importedFiles.length === 0 || (!hasCartItems && !hasCheckoutItems)) {
                return { replaced: false, count: 0 }
            }

            const replacementLookup = new Map();

            importedFiles.forEach((file) => {
                const replacementFor = file?.action?.replacementFor;
                const replacementKid = Number(replacementFor?.kid);
                const replacementBid = toLookupKey(replacementFor?.bid);
                const replacementJid = toLookupKey(replacementFor?.jid);

                if (!Number.isFinite(replacementKid)) return

                if (replacementJid) {
                    replacementLookup.set(`${replacementKid}:jid:${replacementJid}`, file);
                }

                if (replacementBid) {
                    replacementLookup.set(`${replacementKid}:bid:${replacementBid}`, file);
                }
            });

            if (replacementLookup.size === 0) {
                return { replaced: false, count: 0 }
            }

            const replaceInMediaList = (mediaList) => {
                if (!Array.isArray(mediaList)) {
                    return { list: mediaList, replacedCount: 0, changed: false }
                }

                let replacedCount = 0;
                let changed = false;

                const nextList = mediaList.map((mediaEntry) => {
                    const mediaKid = Number(mediaEntry?.kid);
                    const mediaBid = toLookupKey(mediaEntry?.bid);
                    const mediaJid = toLookupKey(mediaEntry?.jid);

                    if (!Number.isFinite(mediaKid)) return mediaEntry

                    const replacement = (mediaJid && replacementLookup.get(`${mediaKid}:jid:${mediaJid}`))
                        || (mediaBid && replacementLookup.get(`${mediaKid}:bid:${mediaBid}`));
                    if (!replacement) return mediaEntry

                    changed = true;
                    replacedCount += 1;

                    if (mediaEntry?.bid || replacement?.bid || mediaEntry?.jid || replacement?.jid) {
                        const fromRef = mediaJid ? `jid:${mediaEntry?.jid ?? mediaJid}` : `bid:${mediaEntry?.bid ?? mediaBid}`;
                        const toRef = replacement?.jid ? `jid:${replacement.jid}` : `bid:${replacement?.bid ?? mediaEntry?.bid ?? mediaBid}`;
                        logger.info(`[session.store] replaced cart media kid:${mediaKid} ${fromRef} -> kid:${replacement?.kid} ${toRef}`);
                    }

                    return {
                        ...(mediaEntry || {}),
                        ...replacement
                    }
                });

                return {
                    list: changed ? nextList : mediaList,
                    replacedCount,
                    changed
                }
            };

            const replacementRevisions = new Map();

            const replaceInCartItems = (cartItems, reuseCartRevisions = false) => {
                let replacedCount = 0;
                let hasChanges = false;

                const nextItems = cartItems.map((cartItem) => {
                    if (!cartItem || typeof cartItem !== 'object') return cartItem

                    let itemChanged = false;
                    let replacedInItem = 0;
                    let nextItem = cartItem;

                    const mediaResult = replaceInMediaList(cartItem.media);
                    const imagesResult = replaceInMediaList(cartItem.images);

                    if (mediaResult.changed || imagesResult.changed) {
                        nextItem = {
                            ...nextItem,
                            media: mediaResult.list,
                            images: imagesResult.list
                        };
                        itemChanged = true;
                    }

                    replacedInItem += mediaResult.replacedCount;
                    replacedInItem += imagesResult.replacedCount;

                    const selectorState = cartItem.selectorState;
                    if (selectorState && typeof selectorState === 'object') {
                        const selectorMediaResult = replaceInMediaList(selectorState.media);
                        const selectorImagesResult = replaceInMediaList(selectorState.images);

                        if (selectorMediaResult.changed || selectorImagesResult.changed) {
                            nextItem = {
                                ...nextItem,
                                selectorState: {
                                    ...selectorState,
                                    media: selectorMediaResult.list,
                                    images: selectorImagesResult.list
                                }
                            };
                            itemChanged = true;
                        }

                        replacedInItem += selectorMediaResult.replacedCount;
                        replacedInItem += selectorImagesResult.replacedCount;
                    }

                    if (replacedInItem > 0) {
                        const previousRevision = cartItem.revision;
                        const nextRevision = reuseCartRevisions && previousRevision
                            ? replacementRevisions.get(previousRevision) || generateShortUUID()
                            : generateShortUUID();

                        if (!reuseCartRevisions && previousRevision) {
                            replacementRevisions.set(previousRevision, nextRevision);
                        }

                        nextItem = {
                            ...nextItem,
                            revision: nextRevision
                        };
                        itemChanged = true;
                    }

                    replacedCount += replacedInItem;
                    if (itemChanged) {
                        hasChanges = true;
                        return nextItem
                    }

                    return cartItem
                });

                return {
                    items: hasChanges ? nextItems : cartItems,
                    replacedCount,
                    changed: hasChanges
                }
            };

            const cartResult = replaceInCartItems(this.cart.items);
            const checkoutResult = replaceInCartItems(this.checkout.cartItems, true);

            if (cartResult.changed) this.cart.items = cartResult.items;
            if (checkoutResult.changed) this.checkout.cartItems = checkoutResult.items;

            const replacedCount = cartResult.replacedCount + checkoutResult.replacedCount;

            return {
                replaced: replacedCount > 0,
                count: replacedCount,
                cartCount: cartResult.replacedCount,
                checkoutCount: checkoutResult.replacedCount
            }
        },

        normalizeCart() {
            const legacyCartId = (typeof this.cartId !== 'undefined' && this.cartId !== null)
                ? this.cartId
                : (this.cart && typeof this.cart === 'object' ? this.cart.cartId : null);

            const legacySessionId = (typeof this.cartSessionId !== 'undefined' && this.cartSessionId !== null)
                ? this.cartSessionId
                : (this.cart && typeof this.cart === 'object' ? this.cart.sessionId : null);

            if (Array.isArray(this.cart)) {
                this.cart = {
                    id: legacyCartId,
                    serverCartId: null,
                    sessionId: legacySessionId,
                    items: this.cart
                };
            }

            if (!this.cart || typeof this.cart !== 'object') {
                this.cart = {
                    id: null,
                    serverCartId: null,
                    sessionId: null,
                    items: []
                };
            }

            if (!Array.isArray(this.cart.items)) {
                this.cart.items = [];
            }

            this.sanitizeCartItemsPrices(this.cart.items);

            if (typeof this.cart.id === 'undefined') {
                this.cart.id = null;
            }

            if (typeof this.cart.serverCartId === 'undefined') {
                this.cart.serverCartId = null;
            }

            if (typeof this.cart.sessionId === 'undefined') {
                this.cart.sessionId = null;
            }

            if (this.cart.id === null && legacyCartId) {
                this.cart.id = legacyCartId;
            }

            if (this.cart.sessionId === null && legacySessionId) {
                this.cart.sessionId = legacySessionId;
            }

            // Legacy Felder nach erfolgreicher Migration entfernen.
            if (typeof this.cartId !== 'undefined') {
                delete this.cartId;
            }
            if (typeof this.cartSessionId !== 'undefined') {
                delete this.cartSessionId;
            }

            // checkout-Felder normalisieren.
            if (!this.checkout || typeof this.checkout !== 'object') {
                this.checkout = { section: "cart", addresses: {}, bankaccount: null, destinationCountry: "DE", email: null, login: null, payment: null, checkoutId: null, cartItems: [] };
            }
            if (typeof this.checkout.bankaccount === 'undefined') {
                this.checkout.bankaccount = null;
            }
            if (typeof this.checkout.login === 'undefined') {
                this.checkout.login = null;
            }
            if (typeof this.checkout.selectStandardInvoiceAddressAfterLogin !== 'boolean') {
                this.checkout.selectStandardInvoiceAddressAfterLogin = false;
            }
            if (typeof this.checkout.payment === 'undefined') {
                this.checkout.payment = null;
            }
            if (typeof this.checkout.checkoutId === 'undefined') {
                this.checkout.checkoutId = null;
            }
            if (!Array.isArray(this.checkout.cartItems)) {
                this.checkout.cartItems = [];
            }

            this.sanitizeCartItemsPrices(this.checkout.cartItems);
        },

        /**
         * Stellt sicher, dass alle Cart-Items eine revision haben.
         * Gibt true zurück, wenn mindestens eine revision neu vergeben wurde.
         */
        ensureItemRevisions() {
            let changed = false;
            if (!Array.isArray(this.cart.items)) return false
            this.cart.items.forEach((item) => {
                if (item && typeof item === 'object' && !item.revision) {
                    item.revision = generateShortUUID();
                    changed = true;
                }
            });
            return changed
        },

        /**
         * Lädt den primären Cart vom Server und synchronisiert lokalen Stand.
         * Strategie: Server-Items gelten als Basis; lokale Items ohne Server-Entsprechung
         * (gleiche pid + optionsHash + Media-Signatur) werden hinzugefügt.
         *
         * @param {boolean} [authoritative=false] Wenn true: Server gewinnt vollständig,
         *   lokale Items werden nicht gemergt. Verwenden beim Laden der Seite oder
         *   beim Betreten des Warenkorbs für eingeloggte Nutzer ohne aktiven Checkout.
         *   Wenn false (Standard, Login): lokale Items werden zum Server gemergt.
         */
        async syncCartFromServer(kid, { authoritative = false } = {}) {
            if (!kid) return

            if (authoritative) {
                await this.flushQueuedCartPush();
            }

            let response = null;
            try {
                response = await ppapiClient.get(`users/${kid}/carts`, null, null, [{ cartRole: 'primary' }]);
            } catch (e) {
                logger.warn('[session.store] syncCartFromServer failed', e);
                return
            }

            const cartsData = typeof response?.data === 'function'
                ? response.data()
                : response;

            if (!cartsData) return

            const serverCart = Array.isArray(cartsData)
                ? (
                    cartsData.find((cartItem) => cartItem?.cartRole === 'primary')
                    || cartsData[0]
                    || null
                )
                : cartsData;

            if (!serverCart || typeof serverCart !== 'object') return

            const serverId = serverCart.id || null;
            let serverItems = [];
            try {
                serverItems = serverCart.productdata ? JSON.parse(serverCart.productdata) : [];
                if (!Array.isArray(serverItems)) serverItems = [];
            } catch (e) {
                logger.warn('[session.store] could not parse server cart productdata', e);
                serverItems = [];
            }

            this.normalizeCart();

            // Revisions in Server-Items sicherstellen.
            serverItems.forEach((item) => {
                if (item && typeof item === 'object' && !item.revision) {
                    item.revision = generateShortUUID();
                }
            });

            this.sanitizeCartItemsPrices(serverItems);

            if (authoritative) {
                // Server ist autoritativ: lokale Items werden vollständig ersetzt.
                // Wird beim Seitenaufruf und beim Betreten des Warenkorbs verwendet.
                this.cart.serverCartId = serverId;
                this.cart.id = serverId;
                this.cart.items = serverItems;
                logger.info(`[session.store] syncCartFromServer (authoritative): replaced cart ${serverId} with ${serverItems.length} items`);
                return
            }

            // Merge-Modus (Standard, z.B. nach Login): lokale Items, die der Server
            // nicht kennt, werden erhalten und zum Server gepusht.
            const getItemSignature = (item) => {
                const pid = item?.product?.pid || '';
                const hash = item?.properties?.optionsHash || '';
                const media = Array.isArray(item?.media)
                    ? item.media.map((m) => m?.id).filter(Boolean).sort().join('|')
                    : '';
                return `${pid}::${hash}::${media}`
            };

            const serverSignatures = new Set(serverItems.map(getItemSignature));
            const localOnlyItems = this.cart.items.filter((item) => !serverSignatures.has(getItemSignature(item)));

            // Lokale Items ohne revision bekommen eine neue.
            localOnlyItems.forEach((item) => {
                if (item && typeof item === 'object' && !item.revision) {
                    item.revision = generateShortUUID();
                }
            });

            const mergedItems = [...serverItems, ...localOnlyItems];
            this.sanitizeCartItemsPrices(mergedItems);

            const previousServerCartId = this.cart.serverCartId;
            const hasActiveCheckout = Boolean(this.checkout?.checkoutId);
            this.cart.serverCartId = serverId;
            // cart.id nur dann umstellen, wenn kein aktiver Checkout es überschreibt.
            if (!hasActiveCheckout || !this.cart.id || this.cart.id === previousServerCartId) {
                this.cart.id = serverId;
            }
            this.cart.items = mergedItems;

            // Lokale Items, die der Server nicht kannte, direkt pushen.
            if (localOnlyItems.length > 0) {
                logger.info(`[session.store] syncCartFromServer: pushing ${localOnlyItems.length} local-only items to server`);
                await this.pushCartToServer(kid);
            }

            logger.info(`[session.store] syncCartFromServer: synced cart ${serverId} with ${mergedItems.length} items`);
        },

        /**
         * Schreibt den aktuellen Cart (items) als primären Server-Cart.
         */
        async pushCartToServer(kid) {
            if (!kid) return

            this.normalizeCart();
            this.sanitizeCartItemsPrices(this.cart.items);

            const productdata = JSON.stringify(this.cart.items);

            const pushTask = (async () => {
                try {
                    if (this.cart.serverCartId) {
                        await ppapiClient.put(`carts/${this.cart.serverCartId}`, {
                            kid,
                            productdata,
                            cartRole: 'primary'
                        });
                    } else {
                        const created = await ppapiClient.post(`carts`, {
                            kid,
                            productdata,
                            cartRole: 'primary'
                        });
                        if (created?.id) {
                            this.cart.serverCartId = created.id;
                            if (!this.cart.id) this.cart.id = created.id;
                        }
                    }
                } catch (e) {
                    logger.warn('[session.store] pushCartToServer failed', e);
                }
            })();

            _activeCartPushPromise = pushTask;
            await pushTask;

            if (_activeCartPushPromise === pushTask) {
                _activeCartPushPromise = null;
            }
        },

        /**
         * Liefert true, wenn ein Cart-Push noch aussteht oder bereits läuft.
         */
        hasQueuedCartPush() {
            return Boolean(_queuedCartPushTimer || _activeCartPushPromise)
        },

        /**
         * Verwirft wartende Cart-Pushes und wartet auf laufende Pushes.
         * Wird vor autoritativen Server-Syncs verwendet, damit kein ausstehender
         * lokaler Push den serverseitigen, autoritativen Cart überschreibt.
         */
        async flushQueuedCartPush() {
            if (_queuedCartPushTimer) {
                clearTimeout(_queuedCartPushTimer);
                _queuedCartPushTimer = null;
                _queuedCartPushKid = null;
                logger.info('[session.store] flushQueuedCartPush: dropped queued push before authoritative sync');
            }

            if (_activeCartPushPromise) {
                await _activeCartPushPromise;
            }
        },

        /**
         * Führt einen sofortigen Server-Push des Carts aus (ohne Debounce).
         */
        scheduleCartPush() {
            const authStore = useAuthStore();
            const kid = authStore.user?.kid;
            if (!kid) return
            this.pushCartToServer(kid);
        },

        /**
         * Plant einen debounced Server-Push des Carts.
         * Nur für Mengenänderungen innerhalb des Warenkorbs verwenden.
         */
        scheduleCartPushDebounced() {
            const authStore = useAuthStore();
            const kid = authStore.user?.kid;
            if (!kid) return

            _queuedCartPushKid = kid;

            if (_queuedCartPushTimer) {
                clearTimeout(_queuedCartPushTimer);
            }

            _queuedCartPushTimer = setTimeout(() => {
                const scheduledKid = _queuedCartPushKid;
                _queuedCartPushTimer = null;
                _queuedCartPushKid = null;

                if (!scheduledKid) return
                this.pushCartToServer(scheduledKid);
            }, CART_PUSH_DEBOUNCE_MS);
        },

        /**
         * Startet den Checkout-Prozess: erzeugt eine checkoutId und speichert
         * einen Snapshot der aktuellen Cart-Items (mit revisions) in checkout.cartItems.
         */
        initCheckout(selectedItems) {
            this.normalizeCart();
            this.checkout.checkoutId = generateShortUUID();
            // Deep copy der Items inkl. revision als Snapshot.
            this.checkout.cartItems = JSON.parse(JSON.stringify(
                Array.isArray(selectedItems) ? selectedItems : this.cart.items
            ));
            this.sanitizeCartItemsPrices(this.checkout.cartItems);
            logger.info(`[session.store] initCheckout: checkoutId=${this.checkout.checkoutId}, items=${this.checkout.cartItems.length}`);
        },

        /**
         * Entfernt nach einer abgeschlossenen Bestellung die bestellten Items aus cart 0,
         * aber nur wenn ihre revision noch unverändert ist (keine Änderung durch anderen Client).
         * @param {Array} orderedItems - Snapshot der Items zum Bestellzeitpunkt (aus checkout.cartItems)
         */
        removeOrderedCartItems(orderedItems) {
            if (!Array.isArray(orderedItems) || orderedItems.length === 0) return

            this.normalizeCart();

            const orderedRevisions = new Set(
                orderedItems.map((item) => item?.revision).filter(Boolean)
            );

            const before = this.cart.items.length;
            this.cart.items = this.cart.items.filter((item) => {
                if (!item?.revision) return true
                return !orderedRevisions.has(item.revision)
            });

            const removed = before - this.cart.items.length;
            logger.info(`[session.store] removeOrderedCartItems: removed ${removed} of ${before} items`);

            // checkout-Snapshot zurücksetzen.
            this.checkout.checkoutId = null;
            this.checkout.cartItems = [];
        },

        resetCart(sessionId = null) {
            this.normalizeCart();
            this.cart = {
                id: null,
                serverCartId: null,
                sessionId: sessionId || null,
                items: []
            };
        },

        enforceGuestCartSession(currentSessionId, forceReset = false) {
            this.normalizeCart();

            if (!currentSessionId) return false

            if (forceReset) {
                this.resetCart(currentSessionId);
                return true
            }

            if (!this.cart.sessionId) {
                this.cart.sessionId = currentSessionId;
                return false
            }

            if (this.cart.sessionId !== currentSessionId) {
                this.resetCart(currentSessionId);
                return true
            }

            return false
        },

        set(par, val) {
            this.normalizeCart();
            if (par == "route" && val != this[par] && typeof this.onRouteChange == "function") this.onRouteChange(val);
            if (typeof this[par] != "undefined") this[par] = val;

            if (par == "cart") {
                this.normalizeCart();
            }
        }
    },
    persist: {
        afterRestore: (ctx) => {
            if (ctx?.store?.normalizeCart) {
                ctx.store.normalizeCart();
            }
        }
    }
});

const expiryTimers = new WeakMap();

function syncRumUser(user) {
    // RUM may be externalized in some builds
    if (typeof openobserveRum?.setUser !== 'function') return

    if (!user) {
        if (typeof openobserveRum.clearUser === 'function') {
            openobserveRum.clearUser();
        }
        return
    }

    const id = user.kid ?? user.id ?? user.email ?? user.username;
    if (!id) return

    openobserveRum.setUser({
        id: String(id),
        name: user.username || user.name || undefined,
        email: user.email || undefined,
    });
}

const useAuthStore = defineStore('auth',{
    state: () => ({
        user: {},
        clientName: "",
        setup: {},
        loginSyncOverlayVisible: false,
        ssoAutoLoginBlocked: false,
        returnUrl: null
    }),
    actions: {
        async onLoginSuccess(previousUser) {
            const emitOverlayState = (visible) => {
                const normalizedVisible = Boolean(visible);
                this.loginSyncOverlayVisible = normalizedVisible;
                if (typeof window === 'undefined') return

                try {
                    dispatchPpShopEvent('pp-shop:login-sync-overlay', { visible: normalizedVisible });
                } catch {
                    // Ignore event-dispatch failures in non-standard host runtimes.
                }
            };

            emitOverlayState(true);

            // Zentraler Hook für Login-Folgelogik (komponentenunabhängig).
            try {
                const result = await runLoginFileImport(this, previousUser);

                if (result?.imported && result?.response) {
                    const sessionStore = useSessionStore();
                    const replacementResult = sessionStore.replaceCartMediaFromImport(result.response);

                    if (replacementResult.replaced) {
                        logger.info(`[auth.store] replaced media after session import: cart=${replacementResult.cartCount}, checkout=${replacementResult.checkoutCount}`);
                    }
                }

                // Cart mit Server synchronisieren (nur bei echtem User-Login).
                const currentKid = this.user?.kid;
                if (currentKid) {
                    const sessionStore = useSessionStore();
                    try {
                        await sessionStore.syncCartFromServer(currentKid);
                    } catch (e) {
                        logger.warn('[auth.store] cart sync after login failed', e);
                    }
                }

                // Neue SessionId generieren, damit nach dem Login hochgeladene Dateien
                // beim nächsten Login erneut importiert werden können.
                this.setup.sessionId = generateUUID();
                return result
            } finally {
                emitOverlayState(false);
            }
        },

        async login(username, password, scope = 'global') {
            try {
                const user = await ppapiClient.auth(username, password);
                await this.storeUser(user);

                // redirect to previous url or default to home page
                return user

            } catch (error) {
                const alertStore = useAlertStore();
                alertStore.error(error, scope);
                return error
            }
        },
        async exchangeSsoToken(token) {
            const configStore = useConfigStore();
            // A double slash bypasses the session/user dbKey prefix in ppapiClient.
            const user = await ppapiClient.post(`/${configStore.apiPath}/sso/exchange`, { token });
            if (!user?.token || !user?.kid || !user?.refresh_token ||
                !Number.isFinite(user?.access?.lifetime) || user.access.lifetime <= 0) {
                throw new Error('Invalid SSO login response')
            }
            await this.storeUser(user);
            return user
        },
        async logout(noRemote) {
            if (!noRemote) {
                // Removing the signal prevents an immediate re-login; a new legacy login sets it again.
                document.cookie = 'user_sso=; Max-Age=0; path=/; domain=kalender-druck.de';
                document.cookie = 'user_sso=; Max-Age=0; path=/';
            }
            if (!noRemote) await ppapiClient.get("logout");
            this.user = null;
            this.expiryListener();
            syncRumUser(null);
        },
        async register(userdata) {
            let response = { ok: true };
            try {
                const user = await ppapiClient.post("register",userdata);
        
                await this.storeUser(user);
                response.user = user;

                return response

            } catch (error) {
                // Alerting bleibt dem Aufrufer überlassen (z.B. für seitenlokalen Alert-Scope).
                response.ok = false;
                response.error = error;
                return response
            }
        },

        storeUser(user) {
            const previousUser = this.user;
            
                // calculate local expiry time
                if (user?.access?.lifetime) {
                    let d = new Date();
                    user.access.expires = new Date(d.getTime() + user.access.lifetime*1000 );
                    
                }

                // update pinia state
                this.user = user;
                this.ssoAutoLoginBlocked = false;
                logger.info(`[auth.store] user ${user?.username} stored`);
                syncRumUser(this.user);
                this.expiryListener();
                return this.onLoginSuccess(previousUser)

        },

        getToken() {
            if (this.user && this.user.access) {
                return this.user.token
            }
            return null
        },
        getUser() {
            if (this.user) {
                return this.user
            }
            return null
        },
        expiryListener() {
            clearTimeout(expiryTimers.get(this));
            expiryTimers.delete(this);
            if (!this.user) return

            let that = this;


            if (this.user.access) {
              let expire_milliseconds = new Date(this.user.access.expires) - new Date();
              if (expire_milliseconds<0) expire_milliseconds = 0;

                logger.info("login expires in " + Math.round(expire_milliseconds/60000) + " Minuten");

                if (!isNaN(expire_milliseconds))
                expiryTimers.set(this, setTimeout(() => {
                    that.refreshLogin();
                },expire_milliseconds));
            }
        },

        async refreshLogin() {

            if (!this.user) return

            let response = await ppapiClient.post("token/refresh",{refresh_token: this.user.refresh_token});

            if (response.token) {
                response.access.expires = new Date(new Date().getTime()  + response.access.lifetime*1000 );
                this.user = response;
                logger.info("user login refreshed");
                syncRumUser(this.user);
                this.expiryListener();
            } else {
                this.user = null;
                logger.info("user logged out");
                syncRumUser(null);
            }

        },

        // Client ID is a unique identifier for the client device.
        // It is supposed to be unchanged across multiple sessions. 

        getClientId() {

            if(!this.setup.clientId) {
                this.setup.clientId = generateUUID();
            }

            return this.setup.clientId

        },

        // Session ID is a unique identifier for the current session. Sessions can be shared over multiple devices.
        
        getSessionId() {

            if(!this.setup.sessionId) {
                this.setup.sessionId = generateUUID();
            }

            return this.setup.sessionId

        },

        setSessionId(sessionId) {

            if(sessionId) {
                this.setup.sessionId = sessionId;
                return true
            }

            return false

        },

        setClientName(name) {

            if(!name) {
                const { browser, os, device } = UAParser(navigator.userAgent );
                let name_parts = [];
                if (device.vendor) {
                    name_parts.push(device.vendor);
                   if (device.type) name_parts.push(device.type);
                   if (device.model) name_parts.push(device.model);
                } else {
                   if (os.name) name_parts.push(os.name);
                   if (browser.name) name_parts.push(browser.name);
                   if (device.type) name_parts.push(device.type);
                }
                this.clientName = name_parts.join(" ");
            } else {
                this.clientName = name;
            }
            
        },

        getClientName() {
            return this.clientName
        }

    },
    persist: true
});

const defaultConfig = {
    baseDir: '/shop',
    service: '',
    contentBase: '',
    contentPaths: {
        fontConfig: '/font-config.js',
        homeTiles: '/home-tiles'
    },
    baseRedirect: false,
    loginSuccessRedirect: {
        method: 'router',
        path: '/shop'
    },
    orderSuccessRedirect: {
        method: 'router',
        path: '/shop'
    },
    app: 'web',
    developerMode: false,
    apiUrl: '',
    apiPath: '/api',
    apiAuthHeader: false,
    defaultDbKey: '',
    authPath: '/authentication_token',
    legacySsoUrl: '',
    registrationSuccessRedirect: '/shop',
    calendarEditPath: '/editor.js',
    dbVersion: 1,
    templateUrl: '',
    hubUrl: '',
    extEditorUrl: '',
    vidge3DUpdateMode: 'end',
    globalNav: true,
    cartBubble: [],
    accountBubble: [],
    uploadButton: [],
    cartInfo: [],
    trackingUrlTemplates: {},
    trackingForceBrowserWindowCarriers: [],
    logger: [],
    accountingYears: [],
    vidgeDebugOverlay: false,
    uploadMimeRestrictions: {},
    xApiToken: 'cHRjh0aXLdNuwB_Xq_rj1j3Z7PdnAHQL6v04-W_wVRM',
};

const useConfigStore = defineStore('config',{
    state: () => ({
        ...defaultConfig,
    }),
    actions: {
        setConfig(config = {}) {
            this.$patch({ ...defaultConfig, ...config, accountingYears: [] });
            return this
        }
    },
    persist: false
});

function normalizeMessage(message) {
    if (message instanceof Error) {
        return { message: message.message, errorCode: message.errorCode };
    }
    if (Array.isArray(message)) return message.map(normalizeMessage);
    if (message === null || message === undefined) return '';
    if (typeof message === 'string' || typeof message === 'number') return String(message);
    if (typeof message === 'object') {
        return {
            message: message.message || message.error || String(message),
            errorCode: message.errorCode
        };
    }
    return String(message);
}

const useAlertStore = defineStore('alert',{
    state: () => ({
        // Alerts nach Scope getrennt ('global' = Default-Anzeige im zentralen Alert).
        alertsByScope: {}
    }),
    actions: {
        success(message, scope = 'global') {
            this.alertsByScope[scope] = { message: normalizeMessage(message), type: 'uk-alert-success' };
        },
        error(message, scope = 'global') {
            this.alertsByScope[scope] = { message: normalizeMessage(message), type: 'uk-alert-danger' };
        },
        clear(scope = 'global') {
            delete this.alertsByScope[scope];
        }
    }
});

const baseUrl = `${import.meta.env.VITE_API_URL}/users`;

const useUsersStore = defineStore('users',{
    state: () => ({
        users: {},
        user: {},
        accountingYears: [],
    }),
    actions: {
        async register(user) {
            await fetchWrapper.post(`${baseUrl}/register`, user);
        },
        async getAll() {
            this.users = { loading: true };
            try {
                this.users = await fetchWrapper.get(baseUrl);
            } catch (error) {
                this.users = { error };
            }
        },
        async getById(id) {
            this.user = { loading: true };
            try {
                this.user = await fetchWrapper.get(`${baseUrl}/${id}`);
            } catch (error) {
                this.user = { error };
            }
        },
        async update(id, params) {
            await fetchWrapper.put(`${baseUrl}/${id}`, params);

            // update stored user if the logged in user updated their own record
            const authStore = useAuthStore();
            if (id === authStore.user.id) {
                // update local storage
                const user = { ...authStore.user, ...params };
                localStorage.setItem('user', JSON.stringify(user));

                // update auth user in pinia state
                authStore.user = user;
            }
        },
        async delete(id) {
            // add isDeleting prop to user being deleted
            this.users.find(x => x.id === id).isDeleting = true;

            await fetchWrapper.delete(`${baseUrl}/${id}`);

            // remove user from list after deleted
            this.users = this.users.filter(x => x.id !== id);

            // auto logout if the logged in user deleted their own record
            const authStore = useAuthStore();
            if (id === authStore.user.id) {
                authStore.logout();
            }
        }
    }
});

const useProdselStore = defineStore('prodsel-modal',{
    state: () => ({
        psm: {
            useProd: {},
            usePgrId: false,
            useMedia: false,
            psId: "ps1",
            bcView: false,
            showModal: false,
            psList: [{ active: true }],
            layout: 'auto'
        }
    }),
    actions: {
        set(par, val) {
            if (this[par]) this[par] = val;
        }
    },
});

export { addressSchema as A, bankaccountSchema as B, isValidEmail as C, DROPZONE_UPLOAD_EVENT as D, registerMercureEvent as E, registerMercureHook as F, registerLocalUploadEvent as G, generateUUID as H, generateShortUUID as I, FOLDER_DEFAULT_SVG as J, FOLDER_CAMERA_SVG as K, FOLDER_CALENDAR_SVG as L, FOLDER_SHARE_SVG as M, FOLDER_SVG_MAP as N, getFolderSvgFallback as O, importSessionImagesForUser as P, importSessionMediaForUser as Q, runLoginFileImport as R, useAuthStore as a, useConfigStore as b, useAlertStore as c, useUsersStore as d, useProdselStore as e, paymentMap as f, globalize as g, ppapiClient as h, getMediaContent as i, uploadModal as j, db as k, dbPopulate as l, mediaModal as m, apiResponse as n, formkitExtension as o, parameter as p, glob as q, getPpShopEventBus as r, dispatchPpShopEvent as s, registerPpShopEvent as t, useSessionStore as u, logger as v, emailSchema as w, fetchWrapper as x, joinProducts as y, fkEl as z };
